import React, { useEffect, useMemo, useState, useRef } from "react";
import { Search, X, MessageSquare, History, ChevronDown, ChevronsUpDown, Eraser } from "lucide-react";
import "./Dashboard.css";
// MMT Task and Booking Task are generated from the NEW metrics dashboards: their task rules
// (metrics, data fields, descriptions, API) are defined in these two files and imported here,
// so the Task page no longer depends on the old MMT Dashboard / Booking Dashboard at all.
import { MMT_METRICS_TASK_SOURCE, MMT_EXTRANET_TASK_SOURCE } from "./MmtMetricsDashboard.jsx";
import { BOOKING_METRICS_TASK_SOURCE, BOOKING_EXTRANET_TASK_SOURCE } from "./BookingMetricsDashboard.jsx";

// Filter dropdowns — the same FilterLabel + MultiSelectFilter the
// Revenue Dashboard uses (copied from Dashboard.jsx, same CSS classes so
// they look identical). Defined here rather than imported because
// Dashboard.jsx already imports Task.jsx (a circular import otherwise).
function FilterLabel({ label, onClear }) {
  return (
    <div className="rd-filter-label-row">
      <label className="rd-filter-label">{label}</label>
      {onClear && (
        <button onClick={onClear} title={`Clear ${label}`} className="rd-clear-btn">
          <Eraser size={12} />
        </button>
      )}
    </div>
  );
}

function MultiSelectFilter({ label, options, selected, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const selectAllRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toggleOption(opt) {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query]);

  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selected.includes(o));
  const someFilteredSelected = filteredOptions.some((o) => selected.includes(o));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  function toggleSelectAll() {
    if (allFilteredSelected) {
      onChange(selected.filter((s) => !filteredOptions.includes(s)));
    } else {
      const merged = new Set([...selected, ...filteredOptions]);
      onChange(Array.from(merged));
    }
  }

  const summary =
    selected.length === 0
      ? "All"
      : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <FilterLabel label={label} onClear={onClear} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <div className="rd-dropdown-search-wrap">
            <Search size={13} className="rd-dropdown-search-icon" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="rd-dropdown-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="rd-dropdown-search-clear"
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {filteredOptions.length > 0 && (
            <label className="rd-option rd-option-selectall">
              <input
                type="checkbox"
                ref={selectAllRef}
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
              />
              <span>Select All{query.trim() ? ` (${filteredOptions.length} shown)` : ""}</span>
            </label>
          )}

          {filteredOptions.length === 0 ? (
            <div className="rd-dropdown-empty">No matches</div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <label
                  key={opt}
                  className={`rd-option${isSelected ? " rd-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOption(opt)}
                  />
                  <span>{opt}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Real property + agent data comes from the same properties-master
// endpoint every other tab (Master Dashboard, Variance) already uses.
// Tasks themselves are generated automatically from Rankly's own rating
// color-coding (see getRatingBucket below, mirrored exactly from
// Dashboard.jsx's getRatingColor) — one task per (property, OTA) where
// that OTA's rating falls outside the "good" bucket (yellow/green/dark
// green). A property with every rating in a good bucket gets zero rows.
// Same pattern for booking/mmt room-nights and rate parity tasks.
//
// Status and closed/reopened state ARE persisted now, shared across every
// logged-in user via taskStatusRoutes.js / taskStatusService.js (a small
// DynamoDB table keyed by each task's deterministic id) — an agent
// marking a task "Under Review" is visible to a super admin on their own
// next load, not just a local browser-only change. Closing (and
// reopening) a task is restricted to Super Admin, enforced on the
// backend, not just hidden client-side.
// "Third Party" is split into WHO the task is waiting on: RevX team, property side or OTA.
const THIRD_PARTY_STATUSES = ["Third Party - RevX Team", "Third Party - Property Side", "Third Party - OTA"];
const STATUS_OPTIONS = ["Pending", "On Hold", "In Progress", ...THIRD_PARTY_STATUSES, "Under Review"];
// The old single "Third Party" status — tasks that already have it keep showing it (it can't be
// picked any more; the next status change moves the task to one of the three above).
const LEGACY_THIRD_PARTY = "Third Party";
// "Rejected" is set ONLY by a manager's Reject action (never picked from
// the dropdown). Once rejected, the assignee moves it on to any normal
// status. Used for filters, summaries and sorting alongside the above.
const REJECTED_STATUS = "Rejected";
const ALL_STATUSES = [REJECTED_STATUS, ...STATUS_OPTIONS, LEGACY_THIRD_PARTY];

// AGENT-LINK FILTERING SUPPORT — same helper duplicated in every other
// dashboard file in this folder (Dashboard.jsx, MainDashboard.jsx,
// VarianceDashboard.jsx). Appends the logged-in user's username so the
// backend's agentFilter.js can restrict results to that user's linked
// agent (OTA EXECUTIVE / OTA MANAGER only).
function apiUrl(path) {
  let username = "";
  try {
    username = localStorage.getItem("loggedInUser") || "";
  } catch {
    username = "";
  }
  if (!username) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}username=${encodeURIComponent(username)}`;
}

// MTD revenue % colour — SAME scale as the Revenue Dashboard
// (Dashboard.jsx getRevenueColor): red < 70% | orange 70–79% |
// yellow 80–89% | light green 90–99% | green 100%+.
function getMtdRevColor(pct) {
  if (pct === null || pct === undefined) return "#e2e8f0";
  if (pct < 70) return "#EF9A9A";
  if (pct < 80) return "#FFCC80";
  if (pct < 90) return "#FFF59D";
  if (pct < 100) return "#A5D6A7";
  return "#66BB6A";
}

function colorForValue(value) {
  const TAG_COLOR_PALETTE = [
    "#fde2e2", "#e0f2fe", "#fef9c3", "#dcfce7", "#ede9fe", "#ffe4e6",
    "#e0e7ff", "#fce7f3", "#d1fae5", "#fef3c7", "#e2e8f0", "#cffafe",
  ];
  if (!value) return undefined;
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return TAG_COLOR_PALETTE[Math.abs(hash) % TAG_COLOR_PALETTE.length];
}

// Up to 2 letters for an agent's avatar circle — first letter of the
// first two words (e.g. "Vaibhav Prajapati" -> "VP"), or just the first
// letter alone for a single-word name. "Unassigned" (the fallback used
// throughout this file whenever a property has no real agent set) shows
// as "?" rather than "UN", since that's not really an agent's initials.
function getAgentInitials(agentName) {
  const s = String(agentName || "").trim();
  if (!s || s === "Unassigned") return "?";
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Expanding task rows — same look as the Occupancy Dashboard's room rows (.occ-subrow).
const TASK_TABLE_CSS = `
/* PROPERTY row: bold, white, dark accent bar on the left */
.task-prop-row { cursor: pointer; }
.task-prop-row td { border-top: 2px solid #dbe3ee !important; }
.task-prop-row td:first-child { box-shadow: inset 4px 0 0 #1e2a78; font-weight: 700; font-size: 13.5px; color: #0f172a; }
.task-prop-row:hover td { background-color: #f4f7ff !important; }
/* An OPEN property = one block: blue header row, light-blue rows inside, the dark bar running
   down the left edge, and a closing line + gap before the next property. */
.task-prop-row.is-open td { background-color: #cbd8f5 !important; border-top: 2px solid #1e2a78 !important; }
.task-open-block td:first-child { box-shadow: inset 4px 0 0 #1e2a78; }
.task-block-end td { height: 10px; padding: 0 !important; background: #fff !important;
  border-top: 2px solid #1e2a78 !important; box-shadow: none !important; }
/* TASK-TYPE row: coloured label, indented */
.task-kind-row td { background: #eaf0fc; padding: 7px 16px 5px 28px !important; border-top: 1px solid #d6e0f5; }
.task-kind-label { display: inline-flex; align-items: center; gap: 6px; padding: 2px 10px 2px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.03em; text-transform: uppercase; border: 1px solid; background: #fff; }
.task-kind-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
/* TASK rows: indented, thin line in the task type's colour */
.task-sub-row td { border-top: 1px dashed #d6e0f5; }
.task-sub-name { padding-left: 46px !important; white-space: normal !important; font-size: 12.5px; color: #334155; line-height: 1.45; position: relative; }
.task-sub-name::before { content: ""; position: absolute; left: 30px; top: 6px; bottom: 6px; width: 3px; border-radius: 2px; background: var(--kind-color, #cbd5e1); }
.task-sub-cell { padding: 6px 12px !important; font-size: 12px; vertical-align: middle; }
.task-sub-status { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.task-sub-status .task-comment-btn { margin-left: auto; }
.task-age { display: inline-block; margin-left: 6px; padding: 0 7px; border-radius: 999px; font-size: 10.5px; font-weight: 600; background: #f1f5f9; color: #475569; white-space: nowrap; }
.task-age.is-old { background: #fee2e2; color: #991b1b; }
.task-age.is-done { background: #dcfce7; color: #166534; }
@media (max-width: 768px) {
  .task-sub-name { padding-left: 26px !important; font-size: 11.5px; }
  .task-sub-name::before { left: 12px; }
  .task-kind-row td { padding-left: 12px !important; }
}
`;
// FILTER BAR on one line (desktop, 100% zoom): flexible widths, compact % Rev buttons, the
// "Show closed tasks" checkbox and "Clear all filters" on the same row. Phones (≤ 768px) keep
// the collapsible Filters panel unchanged; smaller laptops (< 1280px) may wrap to 2 lines.
const TASK_FILTER_CSS = `
@media (min-width: 769px) {
  .task-filter-bar { padding: 12px 20px; }
  .task-filter-bar .rd-filter-row { gap: 10px; align-items: flex-end; }
  .task-filter-bar .rd-filter-group { width: auto; flex: 1 1 140px; min-width: 120px; max-width: 200px; }
  .task-filter-bar .rd-filter-group.rd-search-group { flex: 1.4 1 200px; min-width: 170px; max-width: 280px; }
  .task-filter-bar .rd-filter-group.rd-pct-filter-group { width: auto; flex: 0 0 auto; min-width: 0; max-width: none; }
  .task-filter-bar .rd-pct-filter-group > div { flex-wrap: nowrap !important; }
  .task-filter-bar .rd-filter-group.task-closed-group { flex: 0 0 auto; min-width: 0; max-width: none; }
  .task-filter-bar .rd-dropdown-btn { padding: 7px 8px 7px 10px; font-size: 13px; }
  .task-filter-bar .rd-pct-filter-group .rd-dropdown-btn { padding: 7px 7px; }
  .task-filter-bar .rd-search-input-wrap { height: 32px; box-sizing: border-box; }
  .task-filter-bar .rd-search-input { font-size: 13px; padding-top: 0; padding-bottom: 0; }
  .task-filter-bar .rd-dropdown-panel { min-width: 230px; }
  .task-filter-bar .rd-actions { padding-top: 0; margin-left: 0; flex: 0 0 auto; }
  .task-filter-bar .rd-btn-clear { padding: 7px 10px; font-size: 13px; white-space: nowrap; }
}
@media (min-width: 1280px) {
  .task-filter-bar .rd-filter-row { flex-wrap: nowrap; }
}
`;

// colour per task type (label + the thin line on its tasks)
const TASK_KIND_COLORS = {
  newreview: "#d97706", mmtext: "#7c3aed", bookext: "#2563eb", mmtcomp: "#0d9488", rateparity: "#e11d48",
};

// Which section a task belongs in — only TWO sections (agreed with the team):
//   OVERDUE = its due date has passed and it isn't closed (any status change gives the task a
//             fresh start date + due date, so a passed due date means "not moved on in time");
//   TODAY   = everything else: due today or later (Pending, In Progress, ...), and closed tasks.
// Plain string comparison works since YYYY-MM-DD sorts in date order.
function getDueDateBucket(dueDate, todayStr, closed = false) {
  if (!closed && dueDate && dueDate < todayStr) return "overdue";
  return "today";
}

// Matches the reference UI's red/black date-text convention: overdue
// dates render in red, everything else in the normal text color.
function formatDueDateLabel(dueDate) {
  if (!dueDate) return "";
  const d = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dueDate;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Whole days between two YYYY-MM-DD dates (b - a), counted in calendar days.
function daysBetween(a, b) {
  if (!a || !b) return null;
  const ta = Date.UTC(+a.slice(0, 4), +a.slice(5, 7) - 1, +a.slice(8, 10));
  const tb = Date.UTC(+b.slice(0, 4), +b.slice(5, 7) - 1, +b.slice(8, 10));
  return Number.isNaN(ta) || Number.isNaN(tb) ? null : Math.round((tb - ta) / 86400000);
}
// "Nov 2, 2026" + how long the task has been open (or took, once closed)
function taskAgeLabel(createdDate, closedDate, todayStr) {
  const days = daysBetween(createdDate, closedDate || todayStr);
  if (days === null) return "";
  const n = Math.max(0, days);
  if (closedDate) return n === 0 ? "closed same day" : `took ${n} day${n === 1 ? "" : "s"}`;
  return n === 0 ? "created today" : `${n} day${n === 1 ? "" : "s"} open`;
}

// A property row's due date: the range of its tasks' due dates — "Nov 5 – Nov 7, 2026"; one
// date when they're all the same ("Nov 5, 2026"); the year is written once when it's shared.
function formatDueDateRange(minDate, maxDate) {
  if (!minDate) return "";
  if (!maxDate || maxDate === minDate) return formatDueDateLabel(minDate);
  const a = new Date(`${minDate}T00:00:00`);
  const b = new Date(`${maxDate}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return `${minDate} – ${maxDate}`;
  const md = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return a.getFullYear() === b.getFullYear()
    ? `${md(a)} – ${md(b)}, ${b.getFullYear()}`
    : `${formatDueDateLabel(minDate)} – ${formatDueDateLabel(maxDate)}`;
}

// Compact "2 Pending, 1 Under Review" style summary shown on a property's
// single row in the main table, since it can now have several tasks each
// in a different status. Closed tasks are counted under "Closed" rather
// than their last real status text, so the summary makes clear at a
// glance that they're done, not still open under some status.
function summarizePropertyStatuses(tasks) {
  const counts = new Map();
  for (const t of tasks) {
    const label = t.closed ? "Closed" : t.status;
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  const order = [...ALL_STATUSES, "Closed"];
  return order.filter((s) => counts.has(s)).map((s) => ({ status: s, count: counts.get(s) }));
}

// Same platform ordering Rankly's own tab uses (Dashboard.jsx's
// PLATFORM_ORDER) — keeps the popup's OTA row order consistent with what
// the Rankly dashboard itself shows.
const PLATFORM_ORDER = ["agoda", "booking", "makemytrip", "goibibo", "google", "expedia", "hotels", "tripadvisor"];

function platformSortIndex(platform) {
  const i = PLATFORM_ORDER.indexOf(platform);
  return i === -1 ? PLATFORM_ORDER.length : i;
}

// Each OTA's rating is on a different native scale — mirrors Dashboard.jsx's
// RATING_OUT_OF_10_PLATFORMS / RATING_OUT_OF_5_PLATFORMS exactly (that's
// the Rankly tab's own source of truth for these scales, so this tab's
// numbers/colors match it precisely rather than guessing separately).
const RATING_OUT_OF_10_PLATFORMS = new Set(["agoda", "booking", "booking.com", "expedia", "hotels"]);
const RATING_OUT_OF_5_PLATFORMS = new Set(["makemytrip", "mmt", "goibibo", "google", "tripadvisor"]);
const RATING_SCALE_BY_PLATFORM = {}; // built below from the two sets, single source of truth
for (const p of RATING_OUT_OF_10_PLATFORMS) RATING_SCALE_BY_PLATFORM[p] = 10;
for (const p of RATING_OUT_OF_5_PLATFORMS) RATING_SCALE_BY_PLATFORM[p] = 5;

// Mirrors Dashboard.jsx's getRatingColor thresholds exactly, but returns a
// bucket name instead of a hex color — this is what decides whether a
// rating-improvement task gets generated for a given (property, OTA).
// "none" = no rating data at all for this OTA (white cell on the Rankly
// tab) — deliberately NOT treated as needing a task, since there's no
// current rating to show or act on.
function getRatingBucket(platform, rating) {
  if (rating === null || rating === undefined || rating === "") return "none";
  const plat = String(platform || "").trim().toLowerCase();
  // Same colour code as the dashboards, judged on the value as SHOWN
  // (1 decimal):
  //   out of 10: white 0 | red 0.1–6.0 | yellow 6.1–8.0 | light green 8.1–9.0 | dark green 9.1–10
  //   out of 5:  white 0 | red 0.1–3.0 | yellow 3.1–4.0 | light green 4.1–4.5 | dark green 4.6–5.0
  const value = Math.round(Number(rating) * 10) / 10;
  if (!Number.isFinite(value) || !(value > 0)) return "none"; // 0 = white, no task
  if (RATING_OUT_OF_10_PLATFORMS.has(plat)) {
    if (value >= 9.1) return "dark_green";
    if (value >= 8.1) return "green";
    if (value >= 6.1) return "yellow";
    return "red";
  }
  if (RATING_OUT_OF_5_PLATFORMS.has(plat)) {
    if (value >= 4.6) return "dark_green";
    if (value >= 4.1) return "green";
    if (value >= 3.1) return "yellow";
    return "red";
  }
  return "none";
}

// "Good" ratings = green / dark green. Yellow and red need attention
// (used by the Booking checklist's rating check).
const GOOD_RATING_BUCKETS = new Set(["green", "dark_green"]);

// every raw field the Booking Metrics task metrics read (value + market)
const BOOKING_TASK_FIELDS = Array.from(new Set(BOOKING_METRICS_TASK_SOURCE.metrics.flatMap((m) => [m.field, m.marketField])));

// MMT TASK (Visits / Conversion) — REMOVED: those two tasks are covered by the MMT Extranet Task
// below. The MMT performance data (/api/mmt/summary, 15-day sums) is still loaded because the MMT
// Extranet Task's Rule 0 uses the Property Visits sums.

// MMT EXTRANET TASK — generated from the MMT METRICS dashboard's extranet metrics, following
// "MMT – Task Creation Logic". The 8 checks (Spotlight, Content, Cancellation, Inclusions, VDI,
// Promotions, LMR, Sponsor) are defined in MMT_EXTRANET_TASK_SOURCE in MmtMetricsDashboard.jsx.
// Rule 0, checked first: the property's Property Visits must be BELOW comp visits over the last 15
// days (the same 15-day sums the MMT Task uses; equal = not eligible). No visits data = no tasks.
// One task per property per failing check; a check whose input is missing creates no task.
const MMT_VISITS_METRIC = MMT_METRICS_TASK_SOURCE.metrics.find((m) => m.taskKey === "Property Visits")?.metric || "Property Visits";
function buildMmtExtranetTaskRows(propertiesMaster, mmtSumLast15ByMasterId, extranetByMaster, extranetLoaded) {
  const rows = [];
  if (!extranetByMaster) return rows;
  const loaded = (metricKey) => extranetLoaded.has(metricKey);
  for (const p of propertiesMaster) {
    if (!p.master_id || !p.property_name) continue;
    const masterId = String(p.master_id);
    // Rule 0 — visit eligibility
    const visits = mmtSumLast15ByMasterId.get(masterId)?.get(MMT_VISITS_METRIC);
    if (!visits || !(visits.value < visits.competitorValue)) continue;
    const m = extranetByMaster.get(masterId) || {};
    const covered = !!((m.content && m.content.length) || (m.spotlight && m.spotlight.length));
    for (const check of MMT_EXTRANET_TASK_SOURCE.checks) {
      const result = check.evaluate(m, covered, loaded);
      if (!result || !result.create) continue; // missing input, or the check passes -> no task
      rows.push({
        id: `${masterId}::mmtext::${check.key}`,
        kind: "mmtext",
        master_id: masterId,
        booking_id: p.booking_id ? String(p.booking_id) : null,
        metricKey: check.key,
        metricLabel: check.label,
        stateText: result.state,
        value: visits.value,
        market: visits.competitorValue,
        varValue: visits.value - visits.competitorValue,
        date: "last 15 days",
        property: p.property_name,
        agent: p.agent || "Unassigned",
        task: result.task || check.task, // a check can give a more specific text (e.g. no policy at all)
        defaultStatus: "Pending",
      });
    }
  }
  return rows;
}

// BOOKING EXTRANET TASK — generated from the BOOKING METRICS dashboard's extranet metrics, following
// "Booking.com – Visit Result Task Creation Logic". The 4 checks (Genius, Preferred, Inclusions,
// Cancellation Policy) are defined in BOOKING_EXTRANET_TASK_SOURCE in BookingMetricsDashboard.jsx.
// Each check has a gate, checked first (the same 15-day Book data sums the Booking Task uses;
// equal = not eligible; no data = no tasks):
//   "visits" (Visit Result tasks)  — Property Page Visits  < market visits
//   "search" (Search Result tasks) — Search Result Views   < market search results
//   "any"    (Genius, Preferred)    — either gate open; the check is told which gates are open and
//                                     combines both documents' rules into ONE task.
// One task per property per failing check.
const BOOKING_GATES = {
  visits: { label: "Visits", def: BOOKING_METRICS_TASK_SOURCE.metrics.find((m) => m.taskKey === "views") },
  search: { label: "Search views", def: BOOKING_METRICS_TASK_SOURCE.metrics.find((m) => m.taskKey === "searchViews") },
};
function buildBookingExtranetTaskRows(propertiesMaster, visitConversionSumLast15ByBookingId, bookingExtranetByMaster, bookingExtranetLoaded) {
  const rows = [];
  if (!bookingExtranetByMaster) return rows;
  const loaded = (metricKey) => bookingExtranetLoaded.has(metricKey);
  for (const p of propertiesMaster) {
    if (!p.master_id || !p.property_name || !p.booking_id) continue;
    const masterId = String(p.master_id);
    const entry = visitConversionSumLast15ByBookingId.get(String(p.booking_id));
    if (!entry) continue; // no Book data -> no tasks
    // the two gates: property value vs market over the last 15 days
    const gates = {};
    for (const [name, g] of Object.entries(BOOKING_GATES)) {
      if (!g.def) continue;
      const value = Number(entry[g.def.field]) || 0;
      const market = Number(entry[g.def.marketField]) || 0;
      gates[name] = { open: value < market, value, market, label: g.label };
    }
    if (!Object.values(gates).some((g) => g.open)) continue;
    const m = bookingExtranetByMaster.get(masterId) || {};
    const covered = !!((m.genius && m.genius.length) || (m.preferred && m.preferred.length) || (m.property_page_score && m.property_page_score.length));
    const open = { visits: !!gates.visits?.open, search: !!gates.search?.open };
    const amountOf = (g) => `${g.label}: ${g.value.toLocaleString("en-IN")} / ${g.market.toLocaleString("en-IN")}`;
    for (const check of BOOKING_EXTRANET_TASK_SOURCE.checks) {
      const gateName = check.gate || "visits";
      // the gate(s) this check depends on, and whether it is open
      const usedGates = gateName === "any"
        ? ["visits", "search"].filter((n) => gates[n]?.open).map((n) => gates[n])
        : gates[gateName]?.open ? [gates[gateName]] : [];
      if (!usedGates.length) continue; // gate closed (property >= market)
      const gate = usedGates[0];
      const result = check.evaluate(m, covered, loaded, open);
      if (!result || !result.create) continue; // missing input, or the check passes -> no task
      rows.push({
        id: `${masterId}::bookext::${check.key}`,
        kind: "bookext",
        master_id: masterId,
        booking_id: String(p.booking_id),
        metricKey: check.key,
        metricLabel: check.label,
        stateText: result.state,
        gateLabel: gate.label,
        amountText: usedGates.map(amountOf).join(" · "), // e.g. "Visits: 120 / 340 · Search views: 900 / 1,200"
        value: gate.value,
        market: gate.market,
        varValue: gate.value - gate.market,
        date: "last 15 days",
        property: p.property_name,
        agent: p.agent || "Unassigned",
        task: result.task || check.task,
        defaultStatus: "Pending",
      });
    }
  }
  return rows;
}

// MMT COMP Task — a fundamentally different rule from every other task
// builder here: it requires TWO conditions from TWO different data
// sources both to be true before a task is created, per explicit
// request:
//   1. Today's revenue is below 70% of today's target (Revenue
//      Dashboard's own data).
//   2. Today's MMT comp-set rank for that property is NOT 1 (MMT ASP
//      comp data, same data that powers the MMT Comp dashboard tab).
// Either condition alone does NOT create a task — revenue below 70% but
// already ranked #1 in comps still gets no task, exactly as specified,
// even though that may seem to leave a real problem unflagged; missing
// data for EITHER condition also means no task (can't evaluate what
// isn't there), same "no evidence, no task" convention as every other
// builder in this file.
// MMT COMP Task covers TODAY + THE NEXT 7 DAYS (8 dates), evaluated
// separately per date. The revenue threshold DEPENDS ON HOW FAR AHEAD the
// date is (less of a future date is expected to be sold yet):
//   today    below 60% of target      day +4   below 40%
//   tomorrow below 55%                day +5   below 35%
//   day +2   below 50%                day +6   below 30%
//   day +3   below 45%                day +7   below 25%
// Condition 2 (MMT comp rank is not #1) is unchanged.
// Task types only created for properties below 100% MTD revenue
// (Rankly, MMT, Booking and MMT COMP). Rate Parity is not MTD-based.
const MTD_GATED_TASK_KINDS = new Set(["newreview", "mmtext", "bookext", "mmtcomp"]);
const MMT_COMP_REVENUE_THRESHOLDS = [60, 55, 50, 45, 40, 35, 30, 25];
const MMT_COMP_DAYS_AHEAD = MMT_COMP_REVENUE_THRESHOLDS.length; // 8 dates

function buildMmtCompTaskRows(propertiesMaster, revenueByDateByMasterId, mmtCompRankByDateByMasterId, compDates) {
  const rows = [];
  if (!compDates || compDates.length === 0) return rows;
  for (const p of propertiesMaster) {
    if (!p.master_id || !p.property_name) continue;
    const masterId = String(p.master_id);

    compDates.forEach((dateStr, dayIndex) => {
      const threshold = MMT_COMP_REVENUE_THRESHOLDS[dayIndex];
      if (threshold === undefined) return;
      const revEntry = revenueByDateByMasterId.get(dateStr)?.get(masterId);
      if (!revEntry) return; // no target for this date — can't compute a %

      const revenuePct = (revEntry.revenueSum / revEntry.target) * 100;
      if (revenuePct >= threshold) return; // on track for this many days ahead — no task

      const compEntry = mmtCompRankByDateByMasterId.get(dateStr)?.get(masterId);
      if (!compEntry) return; // no comp data for this date — can't evaluate rank
      if (compEntry.rank === 1) return; // already #1 in comps — no task, per the rule as given

      const dateLabel = formatCompDateLabel(dateStr);
      rows.push({
        id: `${masterId}::mmtcomp::${dateStr}`,
        kind: "mmtcomp",
        master_id: masterId,
        booking_id: p.booking_id ? String(p.booking_id) : null,
        revenue: revEntry.revenueSum,
        target: revEntry.target,
        revenuePct,
        rank: compEntry.rank,
        price: compEntry.price,
        date: dateStr,
        property: p.property_name,
        agent: p.agent || "Unassigned",
        task: `Maintain ASP Below Comp Set ASP — ${dateLabel}`,
        defaultStatus: "Pending",
      });
    });
  }
  return rows;
}

// "YYYY-MM-DD" -> ["YYYY-MM-DD", ...] for `count` consecutive calendar
// days starting at startStr. Built with local-date arithmetic (not
// toISOString) so it never shifts a day in IST.
function getConsecutiveDates(startStr, count) {
  if (!startStr) return [];
  const [y, m, d] = startStr.split("-").map(Number);
  const out = [];
  for (let i = 0; i < count; i++) {
    const dt = new Date(y, m - 1, d + i);
    out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`);
  }
  return out;
}

// "2026-10-01" -> "Thu, 1 Oct"
function formatCompDateLabel(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
// dashboard's VAR column/button — is negative (red: behind market) once
// SUMMED over the last 15 available days.
//
// CHANGED: originally compared a single day's VAR (just the latest
// available date). Per explicit request, this now sums the property
// values AND market values separately across the LAST 15 AVAILABLE
// DATES present in the data (same "last N dates that actually have
// data" convention as the Book Data dashboard's own "Last N days"
// control — not 15 raw calendar days, which could span a gap if some
// days are missing from the source), then computes
// VAR = sum(property) − sum(market) per metric. If that SUMMED VAR is
// negative (red, matching the dashboard's own red/down-arrow VAR
// styling), a task is generated — a single bad day no longer triggers a
// task on its own if the last 15 days as a whole are still ahead of
// market.
//
// Same "one task per thing that's actually wrong" pattern used
// elsewhere — a property can get up to 3 of these tasks. A property
// with no Visit & Conversion data at all in the last 15 days gets no
// task either (can't assume anything without real data either way).
// BOOKING TASK (Conversion / Property Page Visits / Search Result Views) — REMOVED: covered by the
// Booking Extranet Task. The Book data 15-day sums (visitConversionSumLast15ByBookingId) are still
// loaded because the Booking Extranet Task's gates (visits, search results) use them.


// One task per property PER UNDERCUTTING OTA — compares the currently
// selected "reference" OTA's price against every OTHER platform's price
// for that same property/date. If another platform is showing a LOWER
// price than the reference OTA, that's a rate parity problem worth
// flagging: e.g. reference = Agoda at ₹1,300, MMT shows ₹1,100 → MMT is
// undercutting the reference price, so a task is generated naming both
// platforms and both prices. A property can get more than one of these
// tasks if MULTIPLE other platforms are all cheaper than the reference.
//
// If a property has no reference OTA selected at all on the Rate Parity
// dashboard, it generates zero tasks; there's nothing to compare against
// for that property specifically — other properties with a selection
// still generate their own tasks independently.
//
// Scoped to TODAY's check-in date, matching every other task's "today"
// convention. If this data source turns out to lag the same way Visit &
// Conversion did, this may need the same "latest available date" fix
// applied to buildBookVarTaskRows above — not applied preemptively here
// since Rate Parity's own dashboard defaults its date range to today,
// suggesting today-scoped data is actually expected to exist.
// MINIMUM DIFFERENCE: a task is only created when the other platform is
// cheaper than the reference OTA by ₹200 or more. Smaller gaps (₹1–₹199)
// are ignored so the Task page isn't flooded with tiny differences.
const RATE_PARITY_MIN_DIFF_RS = 200;

function buildRateParityTaskRows(propertiesMaster, rateParityByPropertyNameDate, referenceOtaByProperty, todayStr) {
  const rows = [];
  if (!todayStr) return rows;

  for (const p of propertiesMaster) {
    if (!p.master_id || !p.property_name) continue;
    const masterId = String(p.master_id);
    // FIXED: was one single referenceOta shared by every property — now
    // each property looks up its OWN selection, so different properties
    // can use different reference platforms at the same time.
    const referenceOta = referenceOtaByProperty[p.property_name];
    if (!referenceOta) continue;
    const otaMap = rateParityByPropertyNameDate.get(`${p.property_name}|${todayStr}`);
    if (!otaMap) continue;

    const refPrice = otaMap.get(referenceOta);
    if (refPrice === undefined || refPrice === null) continue; // reference OTA has no price here — nothing to compare

    for (const [otaName, price] of otaMap.entries()) {
      if (otaName === referenceOta) continue;
      if (price === null || price === undefined) continue;
      const diff = refPrice - price;
      if (diff < RATE_PARITY_MIN_DIFF_RS) continue; // not cheaper by ₹200+ — no task

      rows.push({
        id: `${masterId}::rateparity::${otaName}`,
        kind: "rateparity",
        master_id: masterId,
        referenceOta,
        referencePrice: refPrice,
        comparisonOta: otaName,
        comparisonPrice: price,
        date: todayStr,
        property: p.property_name,
        agent: p.agent || "Unassigned",
        priceDiff: diff,
        task: `${referenceOta} (₹${refPrice.toLocaleString("en-IN")}) vs ${otaName} (₹${price.toLocaleString("en-IN")})`,
        defaultStatus: "Pending",
      });
    }
  }
  return rows;
}


// Local YYYY-MM-DD for "today" — matches the record_date format every
// other dashboard's data already uses.
// All task dates use INDIA TIME (Asia/Kolkata), whatever the viewer's
// computer is set to — a status changed at 11:30 pm IST counts as that day.
const IST_DATE_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
});
function toIstDateStr(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return IST_DATE_FORMAT.format(d); // en-CA = YYYY-MM-DD
}
function getTodayDateStr() {
  return toIstDateStr(new Date());
}

// STATUS-BASED DUE DATES — working days (Mon–Sat) after the task's start
// date, where the start date is when its status last changed (closing
// doesn't count). 0 = due the same working day.
const DUE_WORKING_DAYS_BY_STATUS = {
  Pending: 0,
  Rejected: 0,
  "Under Review": 1,
  "In Progress": 2,
  "Third Party": 3, // old single status
  "Third Party - RevX Team": 3,
  "Third Party - Property Side": 3,
  "Third Party - OTA": 3,
  "On Hold": 7,
};
// Plain calendar arithmetic on YYYY-MM-DD strings (done in UTC so no
// timezone/DST shift can move a date by a day).
function parseDateStr(dateStr) {
  const [y, m, d] = String(dateStr).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}
function formatDateStr(dt) {
  return dt.toISOString().slice(0, 10);
}
// Working days are MONDAY TO SATURDAY — only Sunday is skipped.
function isWeekend(dt) {
  return dt.getUTCDay() === 0; // Sunday
}
// A start date on Sunday moves to Monday, then N working days are added,
// skipping Sundays. e.g. In Progress set Fri -> due Mon (Sat counts,
// Sun skipped); Pending set Sun -> due Mon.
function addWorkingDays(startDateStr, workingDays) {
  const dt = parseDateStr(startDateStr);
  if (Number.isNaN(dt.getTime())) return startDateStr;
  while (isWeekend(dt)) dt.setUTCDate(dt.getUTCDate() + 1);
  let left = workingDays;
  while (left > 0) {
    dt.setUTCDate(dt.getUTCDate() + 1);
    if (!isWeekend(dt)) left -= 1;
  }
  return formatDateStr(dt);
}
function computeAutoDueDate(status, startDateStr) {
  const days = DUE_WORKING_DAYS_BY_STATUS[status] ?? 0;
  return addWorkingDays(startDateStr, days);
}

function formatRating(rating, platform) {
  const n = Number(rating);
  if (!Number.isFinite(n)) return "—";
  const scale = RATING_SCALE_BY_PLATFORM[String(platform || "").toLowerCase()] || 5;
  return `${n.toFixed(1)}/${scale}`;
}

function capitalizePlatform(platform) {
  const s = String(platform || "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Search needs to find a task by common names too, not just the literal
// text baked into row.task. CONFIRMED gap: a rating task's text comes
// from capitalizePlatform(platform), which for platform="makemytrip"
// produces "Makemytrip" — not "MMT", which is what this app calls that
// platform everywhere else (the MMT Dashboard, the "MMT Task" button).
// Searching "MMT" would correctly find MMT Room-Nights tasks (kind
// "mmt") but silently miss every Rankly rating task for MakeMyTrip,
// since "mmt" isn't literally a substring of "makemytrip". Extend this
// list if another platform's common name turns out to diverge from its
// raw platform value the same way.
const PLATFORM_SEARCH_ALIASES = {
  makemytrip: ["mmt"],
};

// Returns everything a search should be able to match against for one
// task row — the literal task text, PLUS any known platform aliases,
// PLUS the exact button label the user actually sees on screen for that
// task's kind (TASK_KIND_MODAL_CONFIG[row.kind].title, defined further
// below — referencing it here is safe even though it's declared later
// in the file, since this function isn't actually CALLED until well
// after the whole module has finished loading).
//
// FIXED: CONFIRMED via testing every single kind's button label against
// its own task text — none of them matched. Searching "Booking (Book)"
// (the exact button label visible on screen) found nothing, even for a
// property that genuinely has 2 of those tasks, because that label is
// pure UI text that was never part of row.task at all — same root
// problem as the MMT alias below, just present on every kind rather than
// one platform. Now every kind's own displayed name is searchable too,
// not just the underlying task sentence.
function getSearchableTaskText(row) {
  let text = row.task;
  const aliases = row.platform ? PLATFORM_SEARCH_ALIASES[row.platform] : null;
  if (aliases) text += " " + aliases.join(" ");
  const kindTitle = TASK_KIND_MODAL_CONFIG[row.kind]?.title;
  if (kindTitle) text += " " + kindTitle;
  return text.toLowerCase();
}

// (The old "Rankly Tasks" type — latest/15-day overall rating — was
// removed on request. The new-review task below is now the "Rankly Task".)

// NEW REVIEWS TASK — mirrors the Rankly dashboard's "New reviews only"
// view, but over the LAST 15 AVAILABLE Rankly dates (same "last 15
// available dates" convention as the MMT / Booking tasks). For each
// (property, OTA):
//   new reviews (sum) = reviews at latest snapshot − reviews just before the window
//   new-review avg    = (latest score × latest reviews − base score × base reviews) ÷ new reviews
// A task is created when that average is YELLOW or RED, i.e. 8.0 or
// below (out of 10) / 4.0 or below (out of 5). LIGHT GREEN and DARK GREEN
// never get a task.
const NEW_REVIEW_WINDOW_DAYS = 15;
const RANKLY_NO_TASK_BUCKETS = new Set(["green", "dark_green"]);
// Task description shown on every Rankly task, with the target for THAT
// platform's scale only — the start of GREEN, since yellow/red get a task
// (Agoda/Booking/Expedia/Hotels: ≥ 8.1★ out of 10;
// MMT/Goibibo/Google/Tripadvisor: ≥ 4.1★ out of 5), followed by the OTA
// and its actual numbers.
const RANKLY_TASK_TARGET = { 10: "8.1", 5: "4.1" };
function ranklyTaskDescription(platform) {
  const scale = RATING_SCALE_BY_PLATFORM[platform] === 5 ? 5 : 10;
  return `Improve Last 15 Days Rating to ≥ ${RANKLY_TASK_TARGET[scale]}★`;
}

// SAME maths as Dashboard.jsx's computeNewReviewStats, so the task always
// matches the ★ score the dashboard shows for the same 15 days:
//   (latest score × latest reviews − base score × base reviews) ÷ new reviews
// kept inside what's possible (1 .. platform max).
const NEW_REVIEW_MIN_SCORE = 1;
function computeNewReviewAvg(prev, cur, platform) {
  const newCount = cur.reviews - prev.reviews;
  if (newCount <= 0) return { newCount };
  const H = 0.05;
  const scale = RATING_SCALE_BY_PLATFORM[platform] || (Math.max(cur.rating, prev.rating) > 5 ? 10 : 5);
  const clamp = (x) => Math.min(scale, Math.max(NEW_REVIEW_MIN_SCORE, x));
  const raw = (cur.rating * cur.reviews - prev.rating * prev.reviews) / newCount;
  const lo = clamp(((cur.rating - H) * cur.reviews - (prev.rating + H) * prev.reviews) / newCount);
  const hi = clamp(((cur.rating + H) * cur.reviews - (prev.rating - H) * prev.reviews) / newCount);
  return { newCount, avg: Math.min(hi, Math.max(lo, raw)), lo, hi };
}

// Decided on the dashboard COLOUR of that ★ score: green / dark green =
// fine (no task); yellow or red = task.
function isNewReviewResultGood(stats, platform) {
  return RANKLY_NO_TASK_BUCKETS.has(getRatingBucket(platform, stats.avg));
}

function describeNewReviewResult(stats, platform) {
  return `avg ${formatRating(stats.avg, platform)}`;
}

// RANKLY TASK ID — one task per property + OTA ("REVX_0023::newreview::booking"). A task the team
// CLOSED is never reopened; if its rating recovered afterwards (recovered_at) and later drops
// below target again, a NEW task is created after it, with its own id
// "<closed task id>::after-YYYY-MM-DD" (the date the rating recovered — fixed, so the id never
// changes). Open tasks are not replaced: the backend reopens them as a fresh cycle
// (updateTaskLifecycle), and Under Review tasks are left for the reviewer.
function currentRanklyTaskId(baseId, taskStatusById) {
  let id = baseId;
  for (let i = 0; i < 20; i += 1) {
    const saved = taskStatusById && taskStatusById[id];
    if (saved && saved.closed && saved.recoveredAt) id = `${id}::after-${String(saved.recoveredAt).slice(0, 10)}`;
    else break;
  }
  return id;
}

function buildNewReviewTaskRows(propertiesMaster, newReviewLast15ByMasterPlatform, taskStatusById) {
  const rows = [];
  for (const p of propertiesMaster) {
    if (!p.master_id || !p.property_name) continue;
    const masterId = String(p.master_id);
    const bookingId = p.booking_id ? String(p.booking_id) : null;
    const platMap = newReviewLast15ByMasterPlatform.get(masterId);
    if (!platMap) continue;

    for (const [platform, stats] of platMap.entries()) {
      if (!stats) continue;
      const id = currentRanklyTaskId(`${masterId}::newreview::${platform}`, taskStatusById);
      const ota = capitalizePlatform(platform);
      const hasNew = stats.newCount > 0;
      const base = {
        id,
        kind: "newreview",
        master_id: masterId,
        booking_id: bookingId,
        property: p.property_name,
        agent: p.agent || "Unassigned",
        platform,
        newCount: Math.max(0, stats.newCount),
        newAvg: hasNew ? stats.avg : null,
        resultText: hasNew ? describeNewReviewResult(stats, platform) : "no new reviews",
        date: "last 15 days",
      };

      // No new review added at all in the last 15 days — that's a task too.
      if (!hasNew) {
        rows.push({
          ...base,
          task: `${ranklyTaskDescription(platform)} — ${ota}`,
          defaultStatus: "Pending",
        });
        continue;
      }

      // Green / dark green over the last 15 days — NO task at all (the old
      // "improved, needs review" row is gone, per request).
      if (isNewReviewResultGood(stats, platform)) continue;

      rows.push({
        ...base,
        task: `${ranklyTaskDescription(platform)} — ${ota}`,
        defaultStatus: "Pending",
      });
    }
  }
  return rows;
}

function statusBadgeStyle(status) {
  const MAP = {
    Pending: { bg: "#fef3c7", color: "#92400e" },
    "On Hold": { bg: "#fee2e2", color: "#991b1b" },
    "In Progress": { bg: "#dbeafe", color: "#1e40af" },
    "Third Party": { bg: "#ede9fe", color: "#5b21b6" },
    "Third Party - RevX Team": { bg: "#ede9fe", color: "#5b21b6" },
    "Third Party - Property Side": { bg: "#fae8ff", color: "#86198f" },
    "Third Party - OTA": { bg: "#e0e7ff", color: "#3730a3" },
    "Under Review": { bg: "#dcfce7", color: "#166534" },
    Rejected: { bg: "#dc2626", color: "#ffffff" },
    Closed: { bg: "#e2e8f0", color: "#334155" },
  };
  return MAP[status] || { bg: "#e2e8f0", color: "#334155" };
}

// tasks passed in should already be filtered to ONE kind (e.g. "rating"
// or "bookvar") by the caller — the modal shows one kind at a time via
// its own dedicated button, rather than a mixed list.
function buildTaskDetailRows(tasks, group, { bookingAdrByBookingId, mmtAspByMaster }) {
  const sortedTasks = [...tasks].sort(
    (a, b) => platformSortIndex(a.platform) - platformSortIndex(b.platform)
  );

  return sortedTasks.map((t) => {
    if (t.kind === "mmtext" || t.kind === "bookext") {
      return {
        taskId: t.id,
        kind: t.kind,
        label: t.metricLabel,
        value: t.stateText,
        amount: t.amountText || `${t.gateLabel ? `${t.gateLabel}: ` : ""}${t.value.toLocaleString("en-IN")} / ${t.market.toLocaleString("en-IN")}`,
        status: t.status,
        closed: t.closed,
        rejection: t.rejection,
        comments: t.comments,
        logs: t.logs,
        dueDate: t.dueDate,
        startDate: t.startDate,
        createdDate: t.createdDate,
        closedDate: t.closedDate,
        autoDueDate: t.autoDueDate,
        dueDateIsManual: t.dueDateIsManual,
        property: t.property,
        agent: t.agent,
        masterId: t.master_id,
        taskText: t.task,
      };
    }

    if (t.kind === "mmtcomp") {
      return {
        taskId: t.id,
        kind: "mmtcomp",
        label: `${t.revenuePct.toFixed(0)}%`,
        value: `Rank ${t.rank}`,
        amount: `₹${t.revenue.toLocaleString("en-IN")} / ₹${t.target.toLocaleString("en-IN")}`,
        status: t.status,
        closed: t.closed,
        rejection: t.rejection,
        comments: t.comments,
        logs: t.logs,
        dueDate: t.dueDate,
        startDate: t.startDate,
        createdDate: t.createdDate,
        closedDate: t.closedDate,
        autoDueDate: t.autoDueDate,
        dueDateIsManual: t.dueDateIsManual,
        property: t.property,
        agent: t.agent,
        masterId: t.master_id,
        taskText: t.task,
      };
    }

    if (t.kind === "rateparity") {
      return {
        taskId: t.id,
        kind: "rateparity",
        label: `${t.referenceOta} vs ${t.comparisonOta}`,
        value: `₹${t.referencePrice.toLocaleString("en-IN")}`,
        amount: `₹${t.comparisonPrice.toLocaleString("en-IN")}`,
        status: t.status,
        closed: t.closed,
        rejection: t.rejection,
        comments: t.comments,
        logs: t.logs,
        dueDate: t.dueDate,
        startDate: t.startDate,
        createdDate: t.createdDate,
        closedDate: t.closedDate,
        autoDueDate: t.autoDueDate,
        dueDateIsManual: t.dueDateIsManual,
        property: t.property,
        agent: t.agent,
        masterId: t.master_id,
        taskText: t.task,
      };
    }

    if (t.kind === "newreview") {
      return {
        taskId: t.id,
        kind: "newreview",
        label: capitalizePlatform(t.platform),
        value: t.resultText,
        amount: `${t.newCount} new`,
        status: t.status,
        closed: t.closed,
        rejection: t.rejection,
        comments: t.comments,
        logs: t.logs,
        dueDate: t.dueDate,
        startDate: t.startDate,
        createdDate: t.createdDate,
        closedDate: t.closedDate,
        autoDueDate: t.autoDueDate,
        dueDateIsManual: t.dueDateIsManual,
        property: t.property,
        agent: t.agent,
        masterId: t.master_id,
        taskText: t.task,
      };
    }

    // Fallback (was the old "Rankly Tasks" rating rows, now removed —
    // no rows of kind "rating" are generated any more).
    const platform = t.platform;
    return {
      taskId: t.id,
      kind: "rating",
      label: capitalizePlatform(platform),
      value: formatRating(t.rating, platform),
      status: t.status,
      closed: t.closed,
      rejection: t.rejection,
      comments: t.comments,
      logs: t.logs,
        dueDate: t.dueDate,
        startDate: t.startDate,
        createdDate: t.createdDate,
        closedDate: t.closedDate,
        autoDueDate: t.autoDueDate,
        dueDateIsManual: t.dueDateIsManual,
        property: t.property,
        agent: t.agent,
        masterId: t.master_id,
        taskText: t.task,
    };
  });
}

// Column headers/title per task kind — a lookup instead of a ternary
// chain. "rating" has only 2 headers (no PRICE/amount column) — the
// table rendering treats a missing third header as "no amount column
// for this kind" rather than needing a separate flag.
const TASK_KIND_MODAL_CONFIG = {
  // The old "Rankly Tasks" (overall rating) type was removed; the
  // new-review task now carries the "Rankly Task" name. Its internal kind
  // stays "newreview" so existing task ids / statuses / comments are kept.
  newreview: { title: "Rankly Task", headers: ["OTA", "NEW REVIEW AVG (15D)", "NEW REVIEWS (15D)"] },
  mmtext: {
    title: "MMT Extranet Task", headers: ["CHECK", "CURRENT STATUS", "VISITS (PROPERTY / COMP)"],
    source: `${MMT_EXTRANET_TASK_SOURCE.dashboard} · ${MMT_EXTRANET_TASK_SOURCE.section}`,
  },
  bookext: {
    title: "Booking Extranet Task", headers: ["CHECK", "CURRENT STATUS", "PROPERTY / MARKET (LAST 15 DAYS)"],
    source: `${BOOKING_EXTRANET_TASK_SOURCE.dashboard} · ${BOOKING_EXTRANET_TASK_SOURCE.section}`,
  },
  mmtcomp: { title: "MMT COMP Task", headers: ["REVENUE % OF TARGET", "COMP RANK", "REVENUE / TARGET"] },
  rateparity: { title: "Rate Parity Task", headers: ["PLATFORMS", "REFERENCE PRICE", "CHEAPER PRICE"] },
};

// Checklist items shown below the matrix table for "Booking Task"
// tasks specifically — one checklist per row, based on that row's own
// metric. "Recent Reviews" on the Conversion checklist specifically is
// conditional — see getChecklistItemsForMetric below — everything else
// here is unconditional.
const BOOK_VAR_CHECKLIST_BASE_ITEMS = {
  Conversion: ["Room Photos", "Photo Sequencing", "Parity", "Inventory"],
  "Property Page Visits": [
    "Cover Photo", "Rating Score", "Price", "Review Count",
    "Inclusions", "Cancellation Policy", "Property Name",
  ],
  "Search Result Views": [
    "Sponsor", "Visibility Booster", "Genius", "Preferred Partner",
    "Target Promotions", "Location Tagging", "Inventory", "Parity",
    "Photo Sequencing", "Room Photos", "Recent Reviews",
  ],
};

// "Recent Reviews" only belongs on the Conversion checklist when this
// property's own Booking.com rating (via Rankly) is genuinely bad — same
// bucket logic and same "except yellow/green/dark green" convention
// already used for rating tasks (getRatingBucket/GOOD_RATING_BUCKETS
// above), reused here rather than reinvented. If the rating is good, or
// there's no rating data at all yet, this item is simply left off —
// deliberately NOT shown for "no data either way", matching how rating
// tasks themselves never fire on missing data.
// MMT Task checklist — from the Zoho "Business Logic" sheet, Gommt
// column of the "Visit Task" and "Con Task" tabs. Only these two MMT
// metrics get a checklist; Room Nights / Revenue / ASP have none.
// Keyed separately from BOOK_VAR_CHECKLIST_BASE_ITEMS because both
// kinds have a metric literally named "Conversion".
function getChecklistItemsForMetric(kind, metricLabel, bookingRatingBucket) {
  if (kind !== "bookvar") return null;
  const base = BOOK_VAR_CHECKLIST_BASE_ITEMS[metricLabel];
  if (!base) return null;
  if (metricLabel === "Conversion") {
    const ratingIsBad = bookingRatingBucket !== "none" && !GOOD_RATING_BUCKETS.has(bookingRatingBucket);
    return ratingIsBad ? ["Recent Reviews", ...base] : base;
  }
  return base;
}

// Activity log (status changes etc.) -- labels and dot colours used by the Logs popup.
const LOG_LABELS = {
  status_change: "changed the status", rejected: "rejected the task", closed: "closed the task",
  reopened: "reopened the task", due_date: "changed the due date", created: "created the task",
};
const LOG_COLORS = {
  "Pending": "#d97706", "In Progress": "#2563eb", "Under Review": "#ca8a04",
  "Third Party": "#7c3aed", "On Hold": "#dc2626", "Rejected": "#dc2626",
};

// Formats a comment's ISO timestamp into a short, readable "live timing"
// — relative for anything recent (so a fresh comment reads naturally,
// e.g. "Just now" / "5m ago"), falling back to an actual date+time once
// it's old enough that relative phrasing stops being useful.
function formatCommentTimestamp(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return then.toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// Comment images — pasted (Ctrl+V) or picked via the file input are both
// funneled through this: downscales to MAX_DIM on the longer side and
// re-encodes as JPEG so a phone screenshot doesn't balloon the stored
// comment. Returns a compressed data URL, or throws if the file still
// can't be brought under COMMENT_IMAGE_MAX_DATA_URL_LENGTH even after a
// second, lower-quality pass — comments are stored inline (see
// addCommentAction below), so an oversized image risks pushing the
// WHOLE task's comment thread past DynamoDB's ~400KB item limit, not
// just this one comment.
//
// FIXED (confirmed real bug, then re-architected): images used to be
// stored INLINE in the DynamoDB comment thread, where up to 3 images
// per comment could add up to ~630KB -- already over DynamoDB's 400KB
// hard per-item cap, surfacing as a raw "Item size has exceeded the
// maximum allowed size" error. Images now get uploaded to MongoDB
// instead (see uploadCommentImage / taskStatusRoutes.js) and the
// comment thread only ever stores a small URL string per image, so
// DynamoDB's limit no longer applies here at all. This cap now only
// needs to stay sane for upload time and MongoDB's (generous, 16MB
// per document) limit, so it can stay far more generous than before.
const COMMENT_IMAGE_MAX_DIM = 1800;
const COMMENT_IMAGE_MAX_DATA_URL_LENGTH = 2000000; // ~1.5MB decoded
function compressImageFileToDataUrl(file, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Couldn't decode that as an image."));
      img.onload = () => {
        let { width, height } = img;
        if (width > COMMENT_IMAGE_MAX_DIM || height > COMMENT_IMAGE_MAX_DIM) {
          const scale = COMMENT_IMAGE_MAX_DIM / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
async function fileToCompressedDataUrl(file) {
  let dataUrl = await compressImageFileToDataUrl(file, 0.72);
  if (dataUrl.length > COMMENT_IMAGE_MAX_DATA_URL_LENGTH) {
    dataUrl = await compressImageFileToDataUrl(file, 0.45); // second, harder pass
  }
  if (dataUrl.length > COMMENT_IMAGE_MAX_DATA_URL_LENGTH) {
    throw new Error("That image is still too large even after compression — try cropping it or pasting a smaller screenshot.");
  }
  return dataUrl;
}


// relies on for role-gated UI (e.g. PropertyList.jsx, SettingsPage.jsx use
// this exact same localStorage("loggedInRole") check). The backend
// enforces the actual due-date/close/reopen restriction independently
// (see taskStatusService.js) — this is only what decides whether to show
// those controls at all, not the real security boundary.
//
// canManageTask covers all THREE roles allowed to change a task's due
// date or close it — Super Admin, Admin, and OTA Manager — per how this
// was specifically asked for. Every other role (agents, OTA Executive,
// etc.) can still change a task's STATUS (move it along toward Under
// Review) but can no longer touch its due date or close it.
const TASK_MANAGE_ROLES = new Set(["SUPER ADMIN", "ADMIN", "OTA MANAGER"]);

function useCurrentUser() {
  return useMemo(() => {
    let username = "";
    let role = "";
    try {
      username = localStorage.getItem("loggedInUser") || "";
      role = localStorage.getItem("loggedInRole") || "";
    } catch {
      // localStorage unavailable — fall back to empty, same as apiUrl() above
    }
    const normalizedRole = role.trim().toUpperCase();
    return {
      username,
      role: normalizedRole,
      isSuperAdmin: normalizedRole === "SUPER ADMIN",
      canManageTask: TASK_MANAGE_ROLES.has(normalizedRole),
    };
  }, []);
}

export default function Task() {
  // Mobile: filters collapse behind a "Filters" toggle, same as Rankly and
  // the other dashboards (desktop always shows them — see Dashboard.css).
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { username: loggedInUser, role: loggedInRole, isSuperAdmin, canManageTask } = useCurrentUser();
  const [propertiesMaster, setPropertiesMaster] = useState([]);
  const [ranklyRows, setRanklyRows] = useState([]);
  const [bookingOverviewRows, setBookingOverviewRows] = useState([]);
  const [mmtRows, setMmtRows] = useState([]);
  // MMT Metrics extranet data ({ metrics, errors }) for the MMT Extranet Task; null until loaded
  const [mmtExtranet, setMmtExtranet] = useState(null);
  // Booking Metrics extranet data ({ metrics, errors }) for the Booking Extranet Task
  const [bookingExtranet, setBookingExtranet] = useState(null);
  const [visitConversionRows, setVisitConversionRows] = useState([]);
  const [rateParityRows, setRateParityRows] = useState([]);
  const [revenueRows, setRevenueRows] = useState([]);
  const [mmtAspRows, setMmtAspRows] = useState([]); // today .. today+6
  const [referenceOtaByProperty, setReferenceOtaByProperty] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  // Multi-select filters (same checkbox dropdowns as the Revenue
  // Dashboard). Empty array = "All".
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState([]); // task-type TITLES, e.g. "MMT Task"
  // Same "% REV" filter as the Revenue Dashboard (≤30/50/70/90 + custom),
  // whole current month — see monthRevPctByMaster.
  const [revPctFilter, setRevPctFilter] = useState({ operator: "lte", value: "" });
  // Shared, persisted status/closed state — keyed by each task's unique
  // id (see buildRatingTaskRows / buildBookVarTaskRows / buildMmtTask-
  // TaskRows). Loaded from the backend (task-status DynamoDB table via
  // taskStatusRoutes.js) so every logged-in user sees the SAME state —
  // an agent marking something "Under Review" is visible to a super admin
  // on their own next load, not just in this one browser tab. Every write
  // (status change, close, reopen) both POSTs to the backend AND updates
  // this state directly, so the UI reflects the change instantly rather
  // than waiting on a refetch.
  const [taskStatusById, setTaskStatusById] = useState({});
  // True once /api/tasks/summary has loaded successfully — new tasks are
  // only registered after that, so a failed load can't make every task
  // look "new".
  const [taskStatusLoaded, setTaskStatusLoaded] = useState(false);
  // Click-to-sort for the STATUS column — same asc/desc toggle pattern
  // used throughout the rest of this app (Dashboard.jsx's
  // handleHeaderClick). Only "status" is wired up as a sortable key for
  // now, per what was actually asked for.
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  // PROPERTY NAME header click = sort by revenue (the % itself is never
  // shown): 1st click ▲ low → high, 2nd ▼ high → low, 3rd back to A–Z.
  function handleRevenueSortClick() {
    if (sortKey !== "revpct") { setSortKey("revpct"); setSortDirection("asc"); }
    else if (sortDirection === "asc") setSortDirection("desc");
    else { setSortKey(null); setSortDirection("asc"); }
  }
  function handleHeaderClick(key) {
    if (sortKey === key) setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDirection("asc"); }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Properties is the one dependency the table itself can't render
    // without; Rankly/Booking/MMT only feed task generation and
    // the "View Tasks" popup, so a failure there shouldn't block the
    // whole tab — those tasks/columns just won't appear for that property.
    // taskStatus failing just means every task falls back to its default
    // status (Pending) and shows as not-closed, same as a brand-new task.
    const endpoints = [
      { key: "properties", url: "/api/properties/master", setter: setPropertiesMaster },
      { key: "rankly", url: "/api/rankly/summary", setter: setRanklyRows },
      { key: "bookingOverview", url: "/api/booking/overview", setter: setBookingOverviewRows },
      { key: "visitConversion", url: BOOKING_METRICS_TASK_SOURCE.endpoint, setter: setVisitConversionRows }, // Booking Metrics
      { key: "mmt", url: MMT_METRICS_TASK_SOURCE.endpoint, setter: setMmtRows }, // MMT Metrics
      { key: "mmtExtranet", url: MMT_EXTRANET_TASK_SOURCE.endpoint, setter: setMmtExtranet }, // MMT Metrics extranet
      { key: "bookingExtranet", url: BOOKING_EXTRANET_TASK_SOURCE.endpoint, setter: setBookingExtranet }, // Booking Metrics extranet
      { key: "rateparity", url: "/api/rateparity/summary", setter: setRateParityRows },
      { key: "taskStatus", url: "/api/tasks/summary", setter: null },
    ];

    Promise.allSettled(
      endpoints.map(({ url }) => fetch(apiUrl(url)).then((res) => res.json()))
    ).then((results) => {
      if (cancelled) return;
      results.forEach((result, i) => {
        const { key, setter } = endpoints[i];
        if (result.status === "fulfilled" && result.value && result.value.success) {
          if (key === "taskStatus") {
            const byId = {};
            for (const item of result.value.data || []) {
              byId[item.task_id] = {
                status: item.status,
                closed: !!item.closed,
                closed_by: item.closed_by,
                closed_at: item.closed_at,
                comments: item.comments || [],
                logs: item.logs || [],
                // Due date typed in by a manager (cleared by the backend on
                // the next status change, so the automatic one takes over).
                dueDate: item.due_date || "",
                // START DATE for the automatic due date. Older records saved
                // before this change don't have status_changed_at, so fall
                // back to their last update / first-seen time.
                statusChangedAt: item.status_changed_at || item.updated_at || item.first_seen_at || "",
                // CREATED = when the task first appeared (first_seen_at); closed_at = when it was closed
                createdAt: item.first_seen_at || "",
                // Set when the task's problem went away (see the lifecycle
                // effect below); cleared when it comes back and restarts.
                recoveredAt: item.recovered_at || "",
                // set by the automatic reminder job when the task passed its deadline
                // (taskReminderService.js); cleared on the next status change
                escalatedTo: item.escalated_to || "",
                escalatedAt: item.escalated_at || "",
                rejection: item.rejected_at
                  ? { by: item.rejected_by, at: item.rejected_at, reason: item.rejection_reason }
                  : null,
              };
            }
            setTaskStatusById(byId);
            setTaskStatusLoaded(true);
          } else if (key === "mmtExtranet" || key === "bookingExtranet") {
            // this API returns { metrics, errors } instead of { data }
            setter({ metrics: result.value.metrics || {}, errors: result.value.errors || {} });
          } else if (setter) {
            setter(result.value.data || []);
          }
        } else if (key === "properties") {
          setError("Failed to load properties.");
        }
        // every other failure (including taskStatus) is silently
        // tolerated — see comment above
      });
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  // Rate Parity's per-property "Platform" selection — same map Rate
  // Parity's own dropdowns read/write (see rateParityHighlightService.js).
  // Polled the same way those dropdowns poll, so a Rate Parity Task here
  // always reflects whichever platform is CURRENTLY selected for each
  // property, even if someone changes it after this page was first
  // loaded.
  useEffect(() => {
    let cancelled = false;
    function fetchReferenceOtas() {
      fetch(apiUrl("/api/rateparity/highlight"))
        .then((res) => res.json())
        .then((json) => {
          if (!cancelled && json.success) setReferenceOtaByProperty(json.highlights || {});
        })
        .catch(() => {});
    }
    fetchReferenceOtas();
    const interval = setInterval(fetchReferenceOtas, 45000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Rankly: latest record_date per (master_id, platform) — mirrors the
  // "keep the freshest date's value" convention Master Dashboard already
  // uses for every other metric. Computed before `rows` since task
  // generation now depends directly on these ratings.
  const ranklyByMasterPlatform = useMemo(() => {
    const m = new Map();
    for (const r of ranklyRows) {
      if (!r.master_id || !r.platform) continue;
      const masterId = String(r.master_id);
      const platform = String(r.platform).trim().toLowerCase();
      if (!m.has(masterId)) m.set(masterId, new Map());
      const platMap = m.get(masterId);
      const existing = platMap.get(platform);
      if (!existing || new Date(r.record_date) > new Date(existing.date)) {
        platMap.set(platform, { rating: r.avg_rating, date: r.record_date });
      }
    }
    return m;
  }, [ranklyRows]);

  // NEW REVIEWS over the last 15 available Rankly dates, per
  // (master_id, platform) — feeds buildNewReviewTaskRows. Compares each
  // property/OTA's latest snapshot in the window against its snapshot
  // just BEFORE the window (so day 1's new reviews count too), falling
  // back to the first snapshot inside the window if there's no earlier
  // history. Grouped by Rankly location_id first, because one master can
  // map to more than one Rankly location; the one with the most new
  // reviews is used.
  const newReviewLast15ByMasterPlatform = useMemo(() => {
    const datesPresent = Array.from(new Set(ranklyRows.map((r) => r.record_date).filter(Boolean))).sort();
    if (datesPresent.length === 0) return new Map();
    const winDates = datesPresent.slice(-NEW_REVIEW_WINDOW_DAYS);
    const windowStart = winDates[0];
    const windowEnd = winDates[winDates.length - 1];

    const snaps = new Map(); // `${master}|${loc}|${plat}` -> [{date, rating, reviews}]
    for (const r of ranklyRows) {
      if (!r.master_id || !r.platform || !r.record_date) continue;
      if (r.avg_rating === null || r.avg_rating === "" || r.avg_rating === undefined) continue;
      const rating = Number(r.avg_rating);
      const reviews = Number(r.total_reviews);
      if (!Number.isFinite(rating) || !Number.isFinite(reviews)) continue;
      const platform = String(r.platform).trim().toLowerCase();
      const key = `${r.master_id}|${r.location_id ?? ""}|${platform}`;
      if (!snaps.has(key)) snaps.set(key, []);
      snaps.get(key).push({ date: r.record_date, rating, reviews });
    }

    const out = new Map();
    for (const [key, list] of snaps.entries()) {
      const [masterId, , platform] = key.split("|");
      list.sort((a, b) => a.date.localeCompare(b.date));
      const inWindow = list.filter((x) => x.date >= windowStart && x.date <= windowEnd);
      if (inWindow.length === 0) continue;
      const latest = inWindow[inWindow.length - 1];
      const before = list.filter((x) => x.date < windowStart);
      const base = before.length > 0 ? before[before.length - 1] : inWindow[0];
      if (base === latest) continue;
      // Same as the dashboard's Total (New reviews only): the ★ score of
      // each day's new reviews, averaged over the 15 days WEIGHTED by how
      // many new reviews each day had. newCount = all reviews ADDED.
      const chain = base === inWindow[0] ? inWindow : [base, ...inWindow];
      let added = 0;
      let scoreSum = 0;
      for (let i = 1; i < chain.length; i++) {
        const diff = chain[i].reviews - chain[i - 1].reviews;
        if (diff <= 0) continue;
        const day = computeNewReviewAvg(chain[i - 1], chain[i], platform);
        added += diff;
        scoreSum += day.avg * diff;
      }
      const stats = added > 0 ? { newCount: added, avg: scoreSum / added } : { newCount: 0 };
      if (!out.has(masterId)) out.set(masterId, new Map());
      const platMap = out.get(masterId);
      const existing = platMap.get(platform);
      if (!existing || stats.newCount > existing.newCount) platMap.set(platform, stats);
    }
    return out;
  }, [ranklyRows]);

  const todayStr = useMemo(() => getTodayDateStr(), []);
  // The 7 dates MMT COMP Task evaluates: today + next 6 days.
  const mmtCompDates = useMemo(() => getConsecutiveDates(todayStr, MMT_COMP_DAYS_AHEAD), [todayStr]);

  // MMT COMP Task's two extra data sources — fetched separately from the
  // main endpoints array above (rather than added to it) because this
  // one needs todayStr in its URL, and todayStr is declared here, after
  // that effect. Revenue rows mirror exactly what the Revenue Dashboard
  // itself fetches (/api/revenue/summary); the MMT ASP/rank rows are
  // scoped to TODAY only (not the whole history) since that's all this
  // task ever looks at.
  useEffect(() => {
    if (!todayStr) return;
    let cancelled = false;
    Promise.allSettled([
      fetch(apiUrl("/api/revenue/summary")).then((res) => res.json()),
      fetch(apiUrl(`/api/mmt/asp/range?from=${mmtCompDates[0]}&to=${mmtCompDates[mmtCompDates.length - 1]}`)).then((res) => res.json()),
    ]).then(([revenueResult, aspResult]) => {
      if (cancelled) return;
      if (revenueResult.status === "fulfilled" && revenueResult.value?.success) {
        setRevenueRows(revenueResult.value.data || []);
      }
      if (aspResult.status === "fulfilled" && aspResult.value?.success) {
        setMmtAspRows(aspResult.value.data || []);
      }
      // Both failures are silently tolerated, same as every other
      // task-feeding endpoint above — MMT COMP Task just won't appear
      // for any property if either fetch fails, rather than blocking
      // the whole Task tab.
    });
    return () => { cancelled = true; };
  }, [todayStr, mmtCompDates]);

  // MMT VAR — mirrors the Book Data 15-day-sum approach exactly: property
  // values and comp (competitor) values are summed SEPARATELY across the
  // last 15 available MMT dates present in the data (same "last N dates
  // that actually have data" convention as both dashboards' own "Last N
  // days" controls, not 15 raw calendar days), per (master_id, metric).
  // buildMmtVarTaskRows then computes VAR = sum(value) − sum(competitor_
  // value) per metric.
  const mmtSumLast15ByMasterId = useMemo(() => {
    const datesPresent = Array.from(new Set(mmtRows.map((r) => r.date).filter(Boolean))).sort();
    const last15 = new Set(datesPresent.slice(-15));
    const m = new Map();
    for (const r of mmtRows) {
      if (!r.master_id || !r.date || !r.metric || !last15.has(r.date)) continue;
      const masterId = String(r.master_id);
      if (!m.has(masterId)) m.set(masterId, new Map());
      const metricMap = m.get(masterId);
      const metricKey = String(r.metric).trim();
      if (!metricMap.has(metricKey)) metricMap.set(metricKey, { value: 0, competitorValue: 0 });
      const entry = metricMap.get(metricKey);
      entry.value += Number(r.value) || 0;
      entry.competitorValue += Number(r.competitor_value) || 0;
    }
    return m;
  }, [mmtRows]);

  // MMT COMP Task, data source 1 — TODAY's revenue attainment per
  // property. Sums `revenue` across every row for (master_id, todayStr)
  // regardless of source (a property's revenue rows are split by source,
  // same as the Revenue Dashboard's own valuesByDateHotel), and takes
  // `target` directly (it's the same value repeated on every source row
  // for that date, not itself summed — matching how the Revenue
  // Dashboard reads it). No target for today at all (0 or missing) means
  // there's no "% of target" to evaluate, so that property is left out
  // of this map entirely rather than treated as 0%.
  const revenueByDateByMasterId = useMemo(() => {
    const wanted = new Set(mmtCompDates);
    const byDate = new Map(); // date -> Map(masterId -> { revenueSum, target })
    for (const r of revenueRows) {
      if (!r.master_id || !r.record_date) continue;
      const dateStr = String(r.record_date).slice(0, 10);
      if (!wanted.has(dateStr)) continue;
      if (!byDate.has(dateStr)) byDate.set(dateStr, new Map());
      const m = byDate.get(dateStr);
      const masterId = String(r.master_id);
      if (!m.has(masterId)) m.set(masterId, { revenueSum: 0, target: 0 });
      const entry = m.get(masterId);
      entry.revenueSum += Number(r.revenue) || 0;
      entry.target = Number(r.target) || entry.target; // same value on every row; last one wins
    }
    for (const m of byDate.values()) {
      for (const [masterId, entry] of m.entries()) {
        if (!entry.target) m.delete(masterId);
      }
    }
    return byDate;
  }, [revenueRows, mmtCompDates]);

  // MMT COMP Task, data source 2 — TODAY's MMT comp-set rank for each
  // property, per date. mmtAspRows has one row per (property, competitor-or-
  // self hotel name) for today, each with its own `rank`; the row that
  // represents the property ITSELF has to be picked out from its
  // competitors' rows first. Mirrors Dashboard.jsx's buildAspByHotel
  // "self" heuristic exactly (a fuzzy substring match between
  // asp_hotel_name and the property's own hotel_name, case-insensitive,
  // in either direction) rather than a separate ad-hoc rule, since
  // that's the one already proven to identify the self row correctly on
  // this same data. No self row found for a property today (comp data
  // not scraped yet, or no textual match) leaves it out of this map
  // entirely — "no evidence" rather than assuming a rank.
  const mmtCompRankByDateByMasterId = useMemo(() => {
    // Group rows by (date, master_id) first — /asp/range returns every
    // date in one flat array, each row carrying its own `date`.
    const grouped = new Map(); // date -> Map(masterId -> rows[])
    for (const r of mmtAspRows) {
      if (!r.master_id || !r.date) continue;
      const dateStr = String(r.date).slice(0, 10);
      if (!grouped.has(dateStr)) grouped.set(dateStr, new Map());
      const byMaster = grouped.get(dateStr);
      const masterId = String(r.master_id);
      if (!byMaster.has(masterId)) byMaster.set(masterId, []);
      byMaster.get(masterId).push(r);
    }
    const byDate = new Map(); // date -> Map(masterId -> { rank, price })
    for (const [dateStr, byMaster] of grouped.entries()) {
      const m = new Map();
      for (const [masterId, compRows] of byMaster.entries()) {
        const lowerHotelName = (compRows[0]?.hotel_name || "").toLowerCase();
        if (!lowerHotelName) continue;
        const selfRow = compRows.find((r) => {
          const nl = (r.asp_hotel_name || "").toLowerCase();
          return nl && (nl.includes(lowerHotelName) || lowerHotelName.includes(nl));
        });
        if (!selfRow || selfRow.rank == null) continue;
        m.set(masterId, { rank: Number(selfRow.rank), price: Number(selfRow.price) || 0 });
      }
      byDate.set(dateStr, m);
    }
    return byDate;
  }, [mmtAspRows]);

  // Book Data VAR — CHANGED from a single-date lookup to a rolling SUM
  // over the last 15 available dates, per explicit request: property
  // values and market values are summed SEPARATELY across those dates
  // per (hotel_code, metric), then buildBookVarTaskRows computes
  // VAR = sum(property) − sum(market). "Last 15 available dates" means
  // the last 15 dates that actually appear in this data (matching the
  // Book Data dashboard's own "Last N days" convention), not 15 raw
  // calendar days, since this source can have gaps.
  const visitConversionSumLast15ByBookingId = useMemo(() => {
    const datesPresent = Array.from(new Set(visitConversionRows.map((r) => r.record_date).filter(Boolean))).sort();
    const last15 = new Set(datesPresent.slice(-15));
    const m = new Map();
    for (const r of visitConversionRows) {
      if (!r.hotel_code || !r.record_date || !last15.has(r.record_date)) continue;
      const key = r.hotel_code;
      // one running sum per field the Booking Metrics task metrics use (value + market)
      if (!m.has(key)) m.set(key, Object.fromEntries(BOOKING_TASK_FIELDS.map((f) => [f, 0])));
      const entry = m.get(key);
      for (const f of BOOKING_TASK_FIELDS) entry[f] += Number(r[f]) || 0;
    }
    return m;
  }, [visitConversionRows]);

  // Rate Parity: for each (property_name, check_in), a map of ota_name ->
  // price. Keyed by property_name (not master_id) because that's the
  // natural key the Rate Parity API itself already joins on — mirrors
  // exactly how RateParityTab's own propertyMeta is built in Dashboard.jsx.
  const rateParityByPropertyNameDate = useMemo(() => {
    const m = new Map();
    for (const r of rateParityRows) {
      if (!r.property_name || !r.check_in || !r.ota_name) continue;
      const key = `${r.property_name}|${r.check_in}`;
      if (!m.has(key)) m.set(key, new Map());
      m.get(key).set(r.ota_name, r.price);
    }
    return m;
  }, [rateParityRows]);

  // Property revenue % of target, MONTH-TO-DATE: the 1st of the current
  // month up to and including TODAY (India time) — both revenue and
  // target. (Previously this used the WHOLE month, 1st–last day, which
  // counted future bookings against the full month's target and made
  // almost every property look behind early in the month.) Used by the
  // MTD badge AND the MTD gate on Rankly/MMT/Booking tasks.
  // Target is the same value on every row of one property+date, so it's
  // counted once per date; revenue is summed across all rows. A property
  // with no target in this period has no % (null).
  const mtdRevPctByMaster = useMemo(() => {
    const monthStart = `${todayStr.slice(0, 7)}-01`; // 1st of this month
    const perMaster = new Map(); // masterId -> { revenue, targetByDate: Map }
    for (const r of revenueRows) {
      if (!r.master_id || !r.record_date) continue;
      const dateStr = String(r.record_date).slice(0, 10);
      if (dateStr < monthStart || dateStr > todayStr) continue; // MTD only: 1st → today
      const masterId = String(r.master_id);
      if (!perMaster.has(masterId)) perMaster.set(masterId, { revenue: 0, targetByDate: new Map() });
      const e = perMaster.get(masterId);
      e.revenue += Number(r.revenue) || 0;
      const t = Number(r.target) || 0;
      if (t) e.targetByDate.set(dateStr, t);
    }
    const out = new Map();
    for (const [masterId, e] of perMaster.entries()) {
      let target = 0;
      for (const t of e.targetByDate.values()) target += t;
      if (target) out.set(masterId, (e.revenue / target) * 100);
    }
    return out;
  }, [revenueRows, todayStr]);

  // "% REV" FILTER / SORT — restored to how it was before: the WHOLE
  // current month (1st → last day), the same number the Revenue Dashboard
  // shows in its default REVENUE view. (The MTD % above is separate and is
  // only used for the MTD badge and the Rankly/MMT/Booking task gate.)
  const monthRevPctByMaster = useMemo(() => {
    const monthPrefix = todayStr.slice(0, 7); // "YYYY-MM"
    const perMaster = new Map(); // masterId -> { revenue, targetByDate: Map }
    for (const r of revenueRows) {
      if (!r.master_id || !r.record_date) continue;
      const dateStr = String(r.record_date).slice(0, 10);
      if (!dateStr.startsWith(monthPrefix)) continue;
      const masterId = String(r.master_id);
      if (!perMaster.has(masterId)) perMaster.set(masterId, { revenue: 0, targetByDate: new Map() });
      const e = perMaster.get(masterId);
      e.revenue += Number(r.revenue) || 0;
      const t = Number(r.target) || 0;
      if (t) e.targetByDate.set(dateStr, t);
    }
    const out = new Map();
    for (const [masterId, e] of perMaster.entries()) {
      let target = 0;
      for (const t of e.targetByDate.values()) target += t;
      if (target) out.set(masterId, (e.revenue / target) * 100);
    }
    return out;
  }, [revenueRows, todayStr]);

  // MMT Metrics extranet rows per hotel: masterId -> { spotlight: [...], content: [...], ... }.
  // Rows carry master_id (filled from properties-master by the backend); hotel_code (= go_mmt_id)
  // is the fallback. A metric whose table failed to load is left out of extranetLoaded, so its
  // check creates no tasks instead of flagging every hotel.
  const { extranetByMaster, extranetLoaded } = useMemo(() => {
    if (!mmtExtranet) return { extranetByMaster: null, extranetLoaded: new Set() };
    const byGoMmt = new Map(propertiesMaster.filter((p) => p.go_mmt_id).map((p) => [String(p.go_mmt_id).trim(), String(p.master_id)]));
    const byMaster = new Map();
    const loadedKeys = new Set();
    for (const [metricKey, list] of Object.entries(mmtExtranet.metrics || {})) {
      if (mmtExtranet.errors && mmtExtranet.errors[metricKey]) continue;
      loadedKeys.add(metricKey);
      for (const r of list || []) {
        const masterId = r.master_id ? String(r.master_id) : byGoMmt.get(String(r.hotel_code || "").trim());
        if (!masterId) continue;
        if (!byMaster.has(masterId)) byMaster.set(masterId, {});
        const entry = byMaster.get(masterId);
        (entry[metricKey] = entry[metricKey] || []).push(r);
      }
    }
    return { extranetByMaster: byMaster, extranetLoaded: loadedKeys };
  }, [mmtExtranet, propertiesMaster]);

  // Booking Metrics extranet rows per hotel: masterId -> { genius: [...], preferred: [...], ... }.
  // The tables may hold more than one day: only each hotel's LATEST snapshot (max dt) is used.
  // Rows carry master_id; the hotel name (= properties-master property_name) is the fallback.
  const { bookingExtranetByMaster, bookingExtranetLoaded } = useMemo(() => {
    if (!bookingExtranet) return { bookingExtranetByMaster: null, bookingExtranetLoaded: new Set() };
    const byName = new Map(propertiesMaster.map((p) => [String(p.property_name || "").trim().toUpperCase(), String(p.master_id)]));
    const byMaster = new Map();
    const loadedKeys = new Set();
    for (const [metricKey, list] of Object.entries(bookingExtranet.metrics || {})) {
      if (bookingExtranet.errors && bookingExtranet.errors[metricKey]) continue;
      loadedKeys.add(metricKey);
      const perHotel = new Map();
      for (const r of list || []) {
        const masterId = r.master_id ? String(r.master_id) : byName.get(String(r.hotel_name || "").trim().toUpperCase());
        if (!masterId) continue;
        if (!perHotel.has(masterId)) perHotel.set(masterId, []);
        perHotel.get(masterId).push(r);
      }
      for (const [masterId, hotelRows] of perHotel) {
        const latest = hotelRows.reduce((mx, r) => (String(r.dt || "") > mx ? String(r.dt || "") : mx), "");
        const snapshot = latest ? hotelRows.filter((r) => String(r.dt || "") === latest) : hotelRows;
        if (!byMaster.has(masterId)) byMaster.set(masterId, {});
        byMaster.get(masterId)[metricKey] = snapshot;
      }
    }
    return { bookingExtranetByMaster: byMaster, bookingExtranetLoaded: loadedKeys };
  }, [bookingExtranet, propertiesMaster]);

  const rows = useMemo(
    () => [
      ...buildNewReviewTaskRows(propertiesMaster, newReviewLast15ByMasterPlatform, taskStatusById),
      ...buildMmtExtranetTaskRows(propertiesMaster, mmtSumLast15ByMasterId, extranetByMaster, extranetLoaded),
      ...buildBookingExtranetTaskRows(propertiesMaster, visitConversionSumLast15ByBookingId, bookingExtranetByMaster, bookingExtranetLoaded),
      ...buildMmtCompTaskRows(propertiesMaster, revenueByDateByMasterId, mmtCompRankByDateByMasterId, mmtCompDates),
      ...buildRateParityTaskRows(propertiesMaster, rateParityByPropertyNameDate, referenceOtaByProperty, todayStr),
    ]
      // MTD GATE: Rankly, MMT and Booking tasks are only created for
      // properties whose month-to-date revenue is BELOW 100% of target
      // (the red / orange / yellow / light-green % REV colours). A property
      // at 100%+ gets none of these three task types. A property with no
      // MTD % (no target / revenue data) keeps its tasks — no evidence it's
      // on target. MMT COMP tasks use this gate too; Rate Parity does not.
      .filter((row) => {
        if (!MTD_GATED_TASK_KINDS.has(row.kind)) return true;
        const pct = mtdRevPctByMaster.get(String(row.master_id));
        return pct === undefined || pct < 100;
      })
      .sort((a, b) =>
        a.property.localeCompare(b.property)
        // MMT COMP tasks for one property: keep them in date order (today first)
        || (a.kind === "mmtcomp" && b.kind === "mmtcomp" ? a.date.localeCompare(b.date) : 0)
        || a.task.localeCompare(b.task))
      .map((row) => {
        const saved = taskStatusById[row.id];
        // Shown exactly as saved. When a Rankly task's rating drops again, the BACKEND decides
        // (updateTaskLifecycle): reopened as Pending with a new start date — or, if the team
        // already submitted it (Under Review) or closed it, left as it is. The page shows the
        // result as soon as the backend answers, so a submitted task never flashes as reset.
        const override = saved;
        return {
          ...row,
          status: override?.status || row.defaultStatus,
          closed: !!override?.closed,
          comments: override?.comments || [],
          logs: override?.logs || [],
          rejection: override?.rejection || null,
          ...(() => {
            // AUTOMATIC DUE DATE from the status (all task types, MMT COMP
            // included): start date = when the status last changed (India
            // time), + working days for the CURRENT status. A manager's
            // hand-typed date wins until the next status change. A brand
            // new task with no saved record yet starts today (it gets
            // registered with today's date right after this — see below).
            const status = override?.status || row.defaultStatus;
            const startDate = (override?.statusChangedAt && toIstDateStr(override.statusChangedAt)) || todayStr;
            const autoDue = computeAutoDueDate(status, startDate);
            return {
              startDate,
              // CREATED date (first appeared) — a brand-new task not saved yet counts from today
              createdDate: (override?.createdAt && toIstDateStr(override.createdAt)) || startDate,
              closedDate: override?.closed && override?.closed_at ? toIstDateStr(override.closed_at) : "",
              autoDueDate: autoDue,
              dueDateIsManual: !!override?.dueDate,
              dueDate: override?.dueDate || autoDue,
            };
          })(),
        };
      }),
    [
      propertiesMaster, newReviewLast15ByMasterPlatform, todayStr,
      mmtSumLast15ByMasterId, extranetByMaster, extranetLoaded, bookingExtranetByMaster, bookingExtranetLoaded,
      revenueByDateByMasterId, mmtCompRankByDateByMasterId, mmtCompDates,
      visitConversionSumLast15ByBookingId,
      rateParityByPropertyNameDate, referenceOtaByProperty,
      taskStatusById, mtdRevPctByMaster,
    ]
  );

  // Automatically moves a Rankly rating task to "Under Review" the
  // moment its rating improves into a good bucket — per explicit
  // request: it shouldn't just silently disappear once fixed. The
  // detection itself happens in buildRatingTaskRows above (it surfaces
  // these as autoResolved rows); this effect is what actually performs
  // the transition, once, the first time each one is seen. Guarded by
  // checking the row's CURRENT status so this settles rather than
  // looping: once updateStatus flips it to "Under Review", row.status
  // will read "Under Review" on the next pass and the condition stops
  // matching.
  useEffect(() => {
    for (const row of rows) {
      if (row.autoResolved && !row.closed && row.status !== "Under Review") {
        updateStatus(row.id, "Under Review");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // REGISTER NEW TASKS — a task with no saved record yet gets one, with
  // today as its start date, so its status-based due date is fixed and can
  // actually go overdue (before this, an untouched task was "due today"
  // again on every page load). Runs only after the saved statuses have
  // loaded, sends each id at most once per page visit, and the backend
  // only ever CREATES missing records (never touches existing ones).
  // NO DUPLICATES: task ids are fixed per property + task type + OTA (and
  // stay date for MMT COMP), so while a task is open the same problem
  // always maps to the SAME task, never a second copy.
  const registeredTaskIdsRef = useRef(new Set());
  useEffect(() => {
    if (!taskStatusLoaded) return;
    const fresh = rows
      .map((r) => r.id)
      .filter((id) => !taskStatusById[id] && !registeredTaskIdsRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => registeredTaskIdsRef.current.add(id));
    const CHUNK = 500;
    for (let i = 0; i < fresh.length; i += CHUNK) {
      const chunk = fresh.slice(i, i + CHUNK);
      fetch(apiUrl("/api/tasks/register"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskIds: chunk }),
      })
        .then((res) => res.json())
        .then((json) => {
          if (!json.success) throw new Error(json.error || "register failed");
          const created = json.data || [];
          if (created.length === 0) return;
          setTaskStatusById((prev) => {
            const next = { ...prev };
            for (const item of created) {
              if (next[item.task_id]) continue; // something newer already arrived
              next[item.task_id] = {
                closed: false,
                comments: [],
                logs: item.logs || [],
                dueDate: "",
                rejection: null,
                statusChangedAt: item.status_changed_at || item.first_seen_at || "",
                createdAt: item.first_seen_at || "",
              };
            }
            return next;
          });
        })
        .catch(() => {
          // Not fatal: the task still shows (start date = today) and will
          // be registered on the next page load. Not retried in a loop.
        });
    }
  }, [rows, taskStatusById, taskStatusLoaded]);

  // RECOVERED / RETURNED Rankly tasks.
  //   recovered — the property/OTA HAS Rankly data for the last 15 days and
  //               the rating is fine now (so no task), but a saved task
  //               record exists: mark it recovered (once).
  //   returned  — the rating is below target again on a task marked
  //               recovered: the backend reopens it as Pending with a new
  //               start date and one comment — unless the team already
  //               submitted it (Under Review) or closed it.
  // Only Rankly tasks, and only from data that actually loaded — if the
  // Rankly fetch fails the map is empty, so nothing is marked recovered.
  // FIXED: this used the task list AFTER the MTD rule (properties at 100%+
  // MTD revenue get no tasks), so a property crossing 100% marked all its
  // Rankly tasks "recovered" although the rating hadn't changed — and on the
  // 1st of the month (MTD back near 0%) they all "came back" and were
  // reopened. It now looks at the RATING only: the Rankly tasks before the
  // MTD rule.
  const lifecycleSentRef = useRef(new Set());
  useEffect(() => {
    if (!taskStatusLoaded) return;
    const shownIds = new Set(
      buildNewReviewTaskRows(propertiesMaster, newReviewLast15ByMasterPlatform, taskStatusById).map((r) => r.id)
    );
    const recovered = [];
    for (const p of propertiesMaster) {
      if (!p.master_id) continue;
      const platMap = newReviewLast15ByMasterPlatform.get(String(p.master_id));
      if (!platMap) continue;
      for (const platform of platMap.keys()) {
        const id = currentRanklyTaskId(`${p.master_id}::newreview::${platform}`, taskStatusById);
        const saved = taskStatusById[id];
        if (!shownIds.has(id) && saved && !saved.recoveredAt) recovered.push(id);
      }
    }
    // closed (completed) tasks are never restarted automatically
    const returned = Array.from(shownIds).filter((id) => taskStatusById[id]?.recoveredAt && !taskStatusById[id]?.closed);
    const sendRecovered = recovered.filter((id) => !lifecycleSentRef.current.has(`rec:${id}`));
    const sendReturned = returned.filter((id) => !lifecycleSentRef.current.has(`ret:${id}`));
    if (sendRecovered.length === 0 && sendReturned.length === 0) return;
    sendRecovered.forEach((id) => lifecycleSentRef.current.add(`rec:${id}`));
    sendReturned.forEach((id) => lifecycleSentRef.current.add(`ret:${id}`));

    fetch(apiUrl("/api/tasks/lifecycle"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recovered: sendRecovered, returned: sendReturned }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) throw new Error(json.error || "lifecycle failed");
        const changed = json.data || [];
        if (changed.length === 0) return;
        setTaskStatusById((prev) => {
          const next = { ...prev };
          for (const item of changed) {
            next[item.task_id] = {
              ...(prev[item.task_id] || {}),
              status: item.status,
              closed: !!item.closed,
              comments: item.comments || [],
              logs: item.logs || [],
              dueDate: item.due_date || "",
              rejection: item.rejected_at
                ? { by: item.rejected_by, at: item.rejected_at, reason: item.rejection_reason }
                : null,
              statusChangedAt: item.status_changed_at || item.updated_at || item.first_seen_at || "",
              createdAt: item.first_seen_at || "",
              closed_at: item.closed_at,
              recoveredAt: item.recovered_at || "",
            };
          }
          return next;
        });
      })
      .catch(() => {
        // Not fatal — retried on the next page load.
      });
  }, [rows, taskStatusById, taskStatusLoaded, propertiesMaster, newReviewLast15ByMasterPlatform]);

  // Every logged-in user (agent or super admin) can move a task between
  // the ordinary statuses — persisted immediately to the backend so
  // everyone else sees the same thing on their own next load, not just a
  // local-only change like before.
  //
  // FIXED: this previously fired the POST and then just .catch()'d
  // network-level failures only — fetch() resolves normally even for a
  // 4xx/5xx response or a {"success": false, ...} body, so a real
  // backend-side failure (e.g. a DynamoDB permission gap on the WRITE
  // actions specifically, separate from the Scan the cache warmer already
  // proved works) was being silently swallowed. The optimistic UI update
  // looked like it saved, then quietly reverted on the next reload with
  // no indication anything had gone wrong. Now actually checks the
  // response and rolls back + alerts on any real failure, exactly like
  // closeTaskAction/reopenTaskAction already did correctly.
  function updateStatus(taskId, newStatus) {
    const previous = taskStatusById[taskId];
    setTaskStatusById((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId], status: newStatus, closed: prev[taskId]?.closed || false,
        // Real status change -> new start date (today) and the manual due
        // date is dropped, same as the backend does.
        ...((prev[taskId]?.status || null) !== newStatus
          ? { statusChangedAt: new Date().toISOString(), dueDate: "" }
          : {}),
        // resubmitting for review clears the "Rejected" note (backend does the same)
        ...(newStatus === "Under Review" ? { rejection: null } : {}),
      },
    }));
    fetch(apiUrl("/api/tasks/update"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, status: newStatus, username: loggedInUser }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: previous }));
          alert(json.error || "Couldn't save this status change — please try again.");
        } else if (json.data?.logs) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: { ...prev[taskId], logs: json.data.logs } }));
        }
      })
      .catch(() => {
        setTaskStatusById((prev) => ({ ...prev, [taskId]: previous }));
        alert("Couldn't save this status change — please check your connection and try again.");
      });
  }

  // Restricted to Super Admin/Admin/OTA Manager — this is the actual
  // feature: an agent can drive a task to "Under Review", but only these
  // roles can mark it fully closed. Enforced for real on the backend
  // (taskStatusService.js); this button simply isn't shown to anyone else,
  // per canManageTask above.
  function closeTaskAction(taskId) {
    const previousRejection = taskStatusById[taskId]?.rejection || null;
    setTaskStatusById((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], closed: true, rejection: null },
    }));
    fetch(apiUrl("/api/tasks/close"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, username: loggedInUser, role: loggedInRole }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          // Roll back the optimistic update if the backend actually
          // refused it (e.g. role somehow wasn't really privileged).
          setTaskStatusById((prev) => ({ ...prev, [taskId]: { ...prev[taskId], closed: false, rejection: previousRejection } }));
          alert(json.error || "Couldn't close this task.");
        } else if (json.data?.logs) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: { ...prev[taskId], logs: json.data.logs } }));
        }
      })
      .catch(() => {
        setTaskStatusById((prev) => ({ ...prev, [taskId]: { ...prev[taskId], closed: false, rejection: previousRejection } }));
        alert("Couldn't close this task — please try again.");
      });
  }

  // APPROVAL WORKFLOW — Reject (manager only). Marks an Under Review task
  // "Rejected" with a required reason; the backend also appends the
  // reason to the comment thread. Waits for the backend (not optimistic)
  // so the updated comment thread can be shown straight away.
  const [rejectingTaskId, setRejectingTaskId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSaving, setRejectSaving] = useState(false);

  function openRejectDialog(taskId) {
    setRejectingTaskId(taskId);
    setRejectReason("");
  }

  function submitReject() {
    const reason = rejectReason.trim();
    if (!rejectingTaskId || !reason || rejectSaving) return;
    setRejectSaving(true);
    const taskId = rejectingTaskId;
    fetch(apiUrl("/api/tasks/reject"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, username: loggedInUser, role: loggedInRole, reason }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          alert(json.error || "Couldn't reject this task.");
          return;
        }
        const item = json.data || {};
        setTaskStatusById((prev) => ({
          ...prev,
          [taskId]: {
            ...prev[taskId],
            status: item.status || REJECTED_STATUS,
            closed: false,
            comments: item.comments || prev[taskId]?.comments || [],
            logs: item.logs || prev[taskId]?.logs || [],
            rejection: { by: item.rejected_by, at: item.rejected_at, reason: item.rejection_reason },
            statusChangedAt: item.status_changed_at || new Date().toISOString(),
            dueDate: item.due_date || "",
          },
        }));
        setRejectingTaskId(null);
        setRejectReason("");
      })
      .catch(() => alert("Couldn't reject this task — please try again."))
      .finally(() => setRejectSaving(false));
  }

  function reopenTaskAction(taskId) {
    const previousReopen = taskStatusById[taskId];
    setTaskStatusById((prev) => ({
      ...prev,
      // Reopen restarts the clock, same as the backend.
      [taskId]: { ...prev[taskId], closed: false, statusChangedAt: new Date().toISOString(), dueDate: "" },
    }));
    fetch(apiUrl("/api/tasks/reopen"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, username: loggedInUser, role: loggedInRole }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: previousReopen }));
          alert(json.error || "Couldn't reopen this task.");
        } else if (json.data?.logs) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: { ...prev[taskId], logs: json.data.logs } }));
        }
      })
      .catch(() => {
        setTaskStatusById((prev) => ({ ...prev, [taskId]: previousReopen }));
        alert("Couldn't reopen this task — please try again.");
      });
  }

  // Posts a new message to a task's comment thread — appends, never
  // overwrites, unlike a typical "edit this field" action. Optimistic:
  // the new message appears in the thread instantly (with this user's
  // own name/timestamp) rather than waiting on the round-trip, same
  // "feels instant" expectation as any chat UI. On success, reconciles
  // with the server's authoritative comments array (handles the rare
  // case where someone else posted to the same thread in between) rather
  // than trusting the optimistic copy forever. On failure, rolls back to
  // before this message was added and tells the user, rather than
  // silently losing it.
  function addCommentAction(taskId, text, images) {
    const previous = taskStatusById[taskId];
    const optimisticComment = {
      text, username: loggedInUser, timestamp: new Date().toISOString(),
      ...(images && images.length > 0 ? { images } : {}),
    };
    setTaskStatusById((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], comments: [...(prev[taskId]?.comments || []), optimisticComment] },
    }));
    fetch(apiUrl("/api/tasks/comment"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, text, username: loggedInUser, images }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.success && json.data) {
          setTaskStatusById((prev) => ({
            ...prev,
            [taskId]: { ...prev[taskId], comments: json.data.comments || [] },
          }));
        } else {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: previous }));
          alert(json.error || "Couldn't post this comment — please try again.");
        }
      })
      .catch(() => {
        setTaskStatusById((prev) => ({ ...prev, [taskId]: previous }));
        alert("Couldn't post this comment — please check your connection and try again.");
      });
  }

  // Sets/changes a task's due date — same optimistic-update-with-rollback
  // pattern as everything else here. The date input itself already shows
  // the new value instantly (it's a controlled input bound to row.dueDate,
  // which reads straight from this same taskStatusById state), so this
  // just needs to persist it and roll back on a real failure.
  function dueDateAction(taskId, dueDate) {
    const previous = taskStatusById[taskId];
    setTaskStatusById((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], dueDate },
    }));
    fetch(apiUrl("/api/tasks/duedate"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, dueDate, username: loggedInUser, role: loggedInRole }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: previous }));
          alert(json.error || "Couldn't save this due date — please try again.");
        } else if (json.data?.logs) {
          setTaskStatusById((prev) => ({ ...prev, [taskId]: { ...prev[taskId], logs: json.data.logs } }));
        }
      })
      .catch(() => {
        setTaskStatusById((prev) => ({ ...prev, [taskId]: previous }));
        alert("Couldn't save this due date — please check your connection and try again.");
      });
  }

  // Booking Dashboard's ADR (adr_revx), latest record_date per hotel_code
  // (= properties-master's booking_id).
  const bookingAdrByBookingId = useMemo(() => {
    const m = new Map();
    for (const r of bookingOverviewRows) {
      if (!r.hotel_code) continue;
      const key = String(r.hotel_code);
      const existing = m.get(key);
      if (!existing || new Date(r.record_date) > new Date(existing.date)) {
        m.set(key, { adr: r.adr_revx, date: r.record_date });
      }
    }
    return m;
  }, [bookingOverviewRows]);

  // MMT's ASP metric, latest date per master_id.
  const mmtAspByMaster = useMemo(() => {
    const m = new Map();
    for (const r of mmtRows) {
      if (!r.master_id) continue;
      if (String(r.metric || "").trim().toUpperCase() !== "ASP") continue;
      const key = String(r.master_id);
      const existing = m.get(key);
      if (!existing || new Date(r.date) > new Date(existing.date)) {
        m.set(key, { asp: r.value, date: r.date });
      }
    }
    return m;
  }, [mmtRows]);

  const agents = useMemo(
    () => Array.from(new Set(rows.map((r) => r.agent))).sort(),
    [rows]
  );

  // Location comes from properties-master (task rows don't carry it).
  const locationByMaster = useMemo(() => {
    const m = new Map();
    for (const p of propertiesMaster) {
      if (p.master_id) m.set(String(p.master_id), p.location || "");
    }
    return m;
  }, [propertiesMaster]);

  const locations = useMemo(
    () => Array.from(new Set(rows.map((r) => locationByMaster.get(String(r.master_id))).filter(Boolean))).sort(),
    [rows, locationByMaster]
  );

  function matchesRevPctFilter(pct) {
    const v = revPctFilter.value === "" ? null : Number(revPctFilter.value);
    if (v === null || !Number.isFinite(v)) return true;
    if (pct === null || pct === undefined) return false;
    switch (revPctFilter.operator) {
      case "lt": return pct < v;
      case "lte": return pct <= v;
      case "eq": return Math.round(pct) === v;
      case "gt": return pct > v;
      case "gte": return pct >= v;
      default: return true;
    }
  }

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (row.closed && !showClosed) return false;
      if (selectedAgents.length && !selectedAgents.includes(row.agent)) return false;
      if (selectedStatuses.length && !selectedStatuses.includes(row.status)) return false;
      if (selectedLocations.length && !selectedLocations.includes(locationByMaster.get(String(row.master_id)))) return false;
      if (selectedTaskTypes.length && !selectedTaskTypes.includes(TASK_KIND_MODAL_CONFIG[row.kind]?.title)) return false;
      if (!q) return true;
      return (
        row.property.toLowerCase().includes(q) ||
        row.agent.toLowerCase().includes(q) ||
        getSearchableTaskText(row).includes(q)
      );
    });
  }, [rows, search, selectedAgents, selectedStatuses, showClosed, selectedLocations, selectedTaskTypes, locationByMaster]);

  const taskTypeOptions = useMemo(
    () => Object.values(TASK_KIND_MODAL_CONFIG).map((cfg) => cfg.title),
    []
  );
  const hasActiveFilters =
    !!search || selectedAgents.length > 0 || selectedStatuses.length > 0 || showClosed ||
    selectedLocations.length > 0 || selectedTaskTypes.length > 0 ||
    revPctFilter.value !== "" || sortKey === "revpct";
  function clearAllFilters() {
    setSearch(""); setSelectedAgents([]); setSelectedStatuses([]); setShowClosed(false);
    setSelectedLocations([]); setSelectedTaskTypes([]); setRevPctFilter({ operator: "lte", value: "" });
    if (sortKey === "revpct") { setSortKey(null); setSortDirection("asc"); }
  }

  // Flat, per-individual-task detail rows — no property grouping at all
  // now, matching the new sectioned-by-due-date layout. group is passed
  // as null since buildTaskDetailRows no longer actually uses it (the
  // old price-lookup code that needed group.booking_id/master_id was
  // removed along with the PRICE column).
  const allDetailRows = useMemo(
    () => buildTaskDetailRows(filteredRows, null, { bookingAdrByBookingId, mmtAspByMaster }),
    [filteredRows, bookingAdrByBookingId, mmtAspByMaster]
  );

  const STATUS_SORT_ORDER = [...ALL_STATUSES, "Closed"];
  const sortedDetailRows = useMemo(() => {
    if (sortKey !== "status") return allDetailRows;
    const arr = [...allDetailRows];
    arr.sort((a, b) => {
      const aStatus = STATUS_SORT_ORDER.indexOf(a.closed ? "Closed" : a.status);
      const bStatus = STATUS_SORT_ORDER.indexOf(b.closed ? "Closed" : b.status);
      const cmp = aStatus - bStatus;
      return sortDirection === "desc" ? -cmp : cmp;
    });
    return arr;
  }, [allDetailRows, sortKey, sortDirection]);

  // Which sections are collapsed (chevron toggled closed) — all expanded
  // by default. Keyed by section name, not stored per-task.
  const [collapsedSections, setCollapsedSections] = useState({});
  function toggleSection(name) {
    setCollapsedSections((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  // Property-level grouping — one row per property, not per task, per
  // FIXED: originally grouped a property's ENTIRE task list into a
  // single row placed by its earliest due date — so pushing just one
  // task's due date forward did nothing visible unless EVERY task for
  // that property was also pushed forward, since the other tasks kept
  // the property pinned under Today/Overdue. Confirmed via real
  // screenshot: 4 tasks still due today, 1 pushed to tomorrow, property
  // stayed under Today with no visible change.
  //
  // Now buckets each INDIVIDUAL TASK by its own due date first, THEN
  // groups by property WITHIN each bucket — so the same property can
  // appear in more than one section at once, each time showing only the
  // tasks that actually belong there. Moving one task to tomorrow now
  // makes that property show up under Upcoming too, with just that one
  // task, while it stays under Today with its other 4.
  const sectionedPropertyGroups = useMemo(() => {
    const bucketMaps = { today: new Map(), overdue: new Map() };
    for (const r of sortedDetailRows) {
      // MMT COMP tasks now follow the same status-based due dates as
      // every other task, so no special-casing here any more.
      const bucket = getDueDateBucket(r.dueDate, todayStr, r.closed);
      const map = bucketMaps[bucket];
      if (!map.has(r.masterId)) {
        const revPct = monthRevPctByMaster.get(String(r.masterId));
        const mtdPct = mtdRevPctByMaster.get(String(r.masterId));
        map.set(r.masterId, {
          masterId: r.masterId, property: r.property, agent: r.agent, tasks: [], minDueDate: r.dueDate, maxDueDate: r.dueDate,
          minCreatedDate: r.createdDate, maxCreatedDate: r.createdDate,
          revPct: revPct === undefined ? null : revPct, // whole month — "% Rev" filter/sort
          mtdPct: mtdPct === undefined ? null : mtdPct, // MTD — badge
        });
      }
      const g = map.get(r.masterId);
      g.tasks.push(r);
      if (r.dueDate < g.minDueDate) g.minDueDate = r.dueDate;
      if (r.dueDate > g.maxDueDate) g.maxDueDate = r.dueDate;
      if (r.createdDate < g.minCreatedDate) g.minCreatedDate = r.createdDate;
      if (r.createdDate > g.maxCreatedDate) g.maxCreatedDate = r.createdDate;
    }
    const sections = {};
    for (const key of ["today", "overdue"]) {
      const groups = Array.from(bucketMaps[key].values()).filter((g) => matchesRevPctFilter(g.revPct));
      if (sortKey === "revpct") {
        // Rev % ascending/descending; properties with no target always last.
        groups.sort((a, b) => {
          if (a.revPct === null && b.revPct === null) return a.property.localeCompare(b.property);
          if (a.revPct === null) return 1;
          if (b.revPct === null) return -1;
          const cmp = a.revPct - b.revPct;
          return (sortDirection === "desc" ? -cmp : cmp) || a.property.localeCompare(b.property);
        });
      } else {
        groups.sort((a, b) => a.property.localeCompare(b.property));
      }
      sections[key] = groups;
    }
    return sections;
  }, [sortedDetailRows, todayStr, monthRevPctByMaster, mtdRevPctByMaster, revPctFilter, sortKey, sortDirection]);

  const totalPropertyRows =
    sectionedPropertyGroups.today.length + sectionedPropertyGroups.overdue.length;
  // DISTINCT properties across all sections — the same property can show
  // under Today AND Overdue AND Upcoming (one row per section), so adding
  // the section counts would count it up to 3 times. The header badge
  // counts each property once.
  const distinctPropertyCount = useMemo(() => {
    const ids = new Set();
    for (const key of ["today", "overdue"]) {
      for (const g of sectionedPropertyGroups[key]) ids.add(String(g.masterId));
    }
    return ids.size;
  }, [sectionedPropertyGroups]);

  // EXPANDED PROPERTIES — like the Occupancy Dashboard: clicking a property row opens its tasks
  // right below it (no popup). Keyed by section + property, because the same property can show
  // under Today AND Overdue, each with only the tasks that belong there.
  const [expandedPropertyKeys, setExpandedPropertyKeys] = useState(() => new Set());
  const propertyKey = (sectionKey, masterId) => `${sectionKey}|${masterId}`;
  function togglePropertyExpanded(sectionKey, masterId) {
    const k = propertyKey(sectionKey, masterId);
    setExpandedPropertyKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }
  const allVisiblePropertyKeys = useMemo(
    () => ["today", "overdue"].flatMap((key) =>
      collapsedSections[key] ? [] : sectionedPropertyGroups[key].map((g) => propertyKey(key, g.masterId))),
    [sectionedPropertyGroups, collapsedSections]
  );
  const allPropertiesExpanded = allVisiblePropertyKeys.length > 0 && allVisiblePropertyKeys.every((k) => expandedPropertyKeys.has(k));
  function toggleExpandAllProperties() {
    setExpandedPropertyKeys(allPropertiesExpanded ? new Set() : new Set(allVisiblePropertyKeys));
  }

  // A property's tasks grouped by task type, in a fixed order (Rankly, MMT Extranet, Booking
  // Extranet, MMT COMP, Rate Parity). Within a type the tasks keep their order (Rankly: by OTA —
  // Agoda, Booking.com, MakeMyTrip, ...).
  const TASK_KIND_ORDER = Object.keys(TASK_KIND_MODAL_CONFIG);
  function groupTasksByKind(tasks) {
    const byKind = new Map();
    for (const r of tasks) {
      if (!byKind.has(r.kind)) byKind.set(r.kind, []);
      byKind.get(r.kind).push(r);
    }
    const rank = (k) => (TASK_KIND_ORDER.indexOf(k) === -1 ? TASK_KIND_ORDER.length : TASK_KIND_ORDER.indexOf(k));
    return Array.from(byKind.keys()).sort((a, b) => rank(a) - rank(b)).map((kind) => ({
      kind,
      title: TASK_KIND_MODAL_CONFIG[kind]?.title || kind,
      source: TASK_KIND_MODAL_CONFIG[kind]?.source || null,
      tasks: byKind.get(kind),
    }));
  }

  // ONE TASK inside an expanded property — task name | its own due date | assignee | its own
  // status (+ Approve / Reject / Reopen, checklist) with the comments button in the right corner.
  // Same controls as the old popup, laid out in the table's columns.
  function renderTaskRow(r) {
    const canClose = canManageTask && !r.closed && r.status === "Under Review";
    const canReopen = canManageTask && r.closed;
    const badge = statusBadgeStyle(r.closed ? "Closed" : r.status);
    const overdue = getDueDateBucket(r.dueDate, todayStr, r.closed) === "overdue";
    const cellBg = overdue ? "#fff1f2" : "#f6f9ff";
    const escalatedTo = !r.closed && taskStatusById[r.taskId]?.escalatedTo;
    const hasChecklist = !!getChecklistItemsForMetric(r.kind, r.label, "none");
    return (
      <tr key={r.taskId} className="occ-subrow task-sub-row task-open-block">
        <td
          className="rd-td-fixed task-sub-name"
          style={{ position: "sticky", left: 0, background: cellBg, "--kind-color": TASK_KIND_COLORS[r.kind] || "#cbd5e1" }}
        >
          <span className="task-sub-text">{r.taskText}</span>
          {escalatedTo && (
            <span
              title="This task passed its deadline and was automatically assigned for review (see the comments)"
              style={{
                marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: "#991b1b", background: "#fee2e2",
                border: "1px solid #fecaca", borderRadius: 999, padding: "0 7px", whiteSpace: "nowrap", fontStyle: "normal",
              }}
            >
              ⚠️ Review: {escalatedTo}
            </span>
          )}
          {r.rejection && !r.closed && r.status !== "Under Review" && (
            <div
              style={{
                marginTop: 4, padding: "3px 7px", borderRadius: 6, fontSize: 11.5, fontStyle: "normal",
                background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b",
              }}
            >
              <strong>Rejected</strong>
              {r.rejection.by ? ` by ${r.rejection.by}` : ""}
              {r.rejection.at ? ` · ${new Date(r.rejection.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
              {r.rejection.reason ? `: ${r.rejection.reason}` : ""}
            </div>
          )}
        </td>
        <td className="task-sub-cell" style={{ background: cellBg, color: "#475569", whiteSpace: "nowrap" }} title={`Created ${formatDueDateLabel(r.createdDate)}${r.closedDate ? ` · closed ${formatDueDateLabel(r.closedDate)}` : ""}`}>
          {formatDueDateLabel(r.createdDate)}
          {(() => {
            const age = daysBetween(r.createdDate, r.closedDate || todayStr);
            const cls = r.closedDate ? " is-done" : age !== null && age >= 7 ? " is-old" : "";
            return <span className={`task-age${cls}`}>{taskAgeLabel(r.createdDate, r.closedDate, todayStr)}</span>;
          })()}
        </td>
        <td className="task-sub-cell" style={{ background: cellBg }}>
          <input
            type="date"
            value={r.dueDate}
            onChange={(e) => dueDateAction(r.taskId, e.target.value)}
            disabled={!canManageTask}
            title={
              (r.dueDateIsManual
                ? `Due date set by a manager (automatic would be ${formatDueDateLabel(r.autoDueDate)}). Resets to automatic on the next status change.`
                : `Automatic: ${r.closed ? "Closed" : r.status} = ${DUE_WORKING_DAYS_BY_STATUS[r.status] ? `${DUE_WORKING_DAYS_BY_STATUS[r.status]} working day(s)` : "same working day"} from ${formatDueDateLabel(r.startDate)} (status last changed).`)
              + (canManageTask ? "" : " Only Super Admin, Admin, or OTA Manager can change the due date.")
            }
            style={{
              padding: "3px 6px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12, fontFamily: "inherit",
              color: overdue ? "#dc2626" : "#334155", fontWeight: overdue ? 700 : 400,
              background: canManageTask ? "#fff" : "#f1f5f9", cursor: canManageTask ? "text" : "not-allowed",
            }}
          />
        </td>
        <td className="task-sub-cell" style={{ background: cellBg }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22,
                borderRadius: "50%", background: colorForValue(r.agent) || "#e2e8f0", fontSize: 9.5, fontWeight: 700, color: "#1e293b",
              }}
            >
              {getAgentInitials(r.agent)}
            </span>
            <span style={{ color: "#334155" }}>{r.agent}</span>
          </span>
        </td>
        <td className="task-sub-cell" style={{ background: cellBg }}>
          <div className="task-sub-status">
            {r.closed ? (
              <span style={{ padding: "3px 9px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: badge.bg, color: badge.color }}>
                Closed
              </span>
            ) : (
              <select
                value={r.status}
                onChange={(e) => handleStatusChange(r, e.target.value)}
                style={{
                  padding: "3px 8px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 12, fontWeight: 600,
                  background: badge.bg, color: badge.color, cursor: "pointer", maxWidth: 210,
                }}
              >
                {r.status === REJECTED_STATUS && <option value={REJECTED_STATUS} disabled>Rejected</option>}
                {r.status === LEGACY_THIRD_PARTY && <option value={LEGACY_THIRD_PARTY} disabled>Third Party</option>}
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status} disabled={status === "Under Review" && !isChecklistComplete(r)}>
                    {status}
                  </option>
                ))}
              </select>
            )}
            {canClose && (
              <>
                <button
                  type="button"
                  onClick={() => closeTaskAction(r.taskId)}
                  title="Approve and close this task (Super Admin, Admin, or OTA Manager only)"
                  style={{ padding: "2px 7px", fontSize: 11, fontWeight: 700, borderRadius: 6, border: "1px solid #86efac", background: "#dcfce7", color: "#166534", cursor: "pointer" }}
                >
                  ✓ Approve
                </button>
                <button
                  type="button"
                  onClick={() => openRejectDialog(r.taskId)}
                  title="Reject — mark this task Rejected with a reason (Super Admin, Admin, or OTA Manager only)"
                  style={{ padding: "2px 7px", fontSize: 11, fontWeight: 700, borderRadius: 6, border: "1px solid #fca5a5", background: "#fee2e2", color: "#b91c1c", cursor: "pointer" }}
                >
                  ✗ Reject
                </button>
              </>
            )}
            {canReopen && (
              <button
                type="button"
                onClick={() => reopenTaskAction(r.taskId)}
                title="Reopen this task (Super Admin, Admin, or OTA Manager only)"
                style={{ padding: "2px 7px", fontSize: 11, fontWeight: 600, borderRadius: 6, border: "1px solid #cbd5e1", background: "#f1f5f9", color: "#334155", cursor: "pointer" }}
              >
                Reopen
              </button>
            )}
            {hasChecklist && (
              <button
                type="button"
                onClick={() => setOpenChecklistTaskId(r.taskId)}
                title={isChecklistComplete(r) ? "Checklist complete" : "Complete this checklist before marking Under Review"}
                style={{
                  padding: "2px 6px", fontSize: 11, fontWeight: 600, borderRadius: 999, border: "1px solid #cbd5e1", cursor: "pointer",
                  whiteSpace: "nowrap", background: isChecklistComplete(r) ? "#dcfce7" : "#f1f5f9", color: isChecklistComplete(r) ? "#166534" : "#334155",
                }}
              >
                ✓ Checklist
              </button>
            )}
            <button
              type="button"
              className="task-comment-btn"
              onClick={() => openCommentThread(r.taskId)}
              title="View / add comments"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer",
                color: (r.comments || []).length > 0 ? "#1e40af" : "#94a3b8", fontSize: 12, fontWeight: 600, padding: 0,
              }}
            >
              <MessageSquare size={15} strokeWidth={2} />
              {(r.comments || []).length > 0 ? r.comments.length : ""}
            </button>
            <button
              type="button"
              className="task-comment-btn"
              onClick={() => setOpenLogsTaskId(r.taskId)}
              title="View status change logs"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none",
                cursor: "pointer", marginLeft: 8, padding: 0, fontSize: 12, fontWeight: 600,
                color: (r.logs || []).length > 0 ? "#6d28d9" : "#94a3b8",
              }}
            >
              <History size={15} strokeWidth={2} />
              {(r.logs || []).length > 0 ? r.logs.length : ""}
            </button>
          </div>
        </td>
      </tr>
    );
  }

  // Checklist popup state — moved here (was previously inside the
  // per-property-per-kind modal) since tasks are no longer grouped by
  // property/kind before being shown; each row is already its own task.
  const [checklistProgress, setChecklistProgress] = useState({});
  const [openChecklistTaskId, setOpenChecklistTaskId] = useState(null);
  const [openCommentTaskId, setOpenCommentTaskId] = useState(null);
  const [openLogsTaskId, setOpenLogsTaskId] = useState(null);
  const [composeText, setComposeText] = useState("");
  const [pendingImages, setPendingImages] = useState([]); // [{ dataUrl, id }]
  const [imageError, setImageError] = useState(null);
  const [processingImages, setProcessingImages] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState(null);
  const commentFileInputRef = useRef(null);
  const replaceFileInputRef = useRef(null); // "Replace" on one attached screenshot
  const replaceTargetIdRef = useRef(null);

  function toggleChecklistItem(taskId, item) {
    setChecklistProgress((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], [item]: !prev[taskId]?.[item] },
    }));
  }

  // A row with no defined checklist (anything other than Booking Task
  // rows and MMT Property Visits / Conversion rows) is treated as always
  // "complete" — the Under-Review gate below only restricts those.
  function isChecklistComplete(row) {
    let bookingRatingBucket = "none";
    if (row.kind === "bookvar") {
      const bookingRatingEntry = ranklyByMasterPlatform.get(String(row.masterId))?.get("booking");
      bookingRatingBucket = getRatingBucket("booking", bookingRatingEntry?.rating);
    }
    const items = getChecklistItemsForMetric(row.kind, row.label, bookingRatingBucket);
    if (!items) return true;
    const progress = checklistProgress[row.taskId] || {};
    return items.every((item) => progress[item]);
  }

  function handleStatusChange(row, newStatus) {
    if (newStatus === REJECTED_STATUS) return; // only a manager's Reject sets this
    if (newStatus === "Under Review" && !isChecklistComplete(row)) {
      alert("Complete the checklist for this item before marking it Under Review.");
      return;
    }
    updateStatus(row.taskId, newStatus);
  }

  const MAX_COMMENT_IMAGES = 3;

  // A screenshot is uploaded the moment it's attached (so Send is instant). If it's removed,
  // replaced or the comment box is closed without sending, delete that upload so it doesn't
  // linger. The server refuses to delete an image that's part of a posted comment.
  function discardUploadedImage(url) {
    if (!url || !String(url).startsWith("/api/tasks/comment-image/")) return;
    fetch(apiUrl(url), { method: "DELETE" }).catch(() => {});
  }
  function closeCommentThread() {
    pendingImages.forEach((img) => discardUploadedImage(img.dataUrl));
    setPendingImages([]);
    setOpenCommentTaskId(null);
  }

  function openCommentThread(taskId) {
    setOpenCommentTaskId(taskId);
    setComposeText("");
    setPendingImages([]);
    setImageError(null);
    setPreviewImageSrc(null);
  }

  // Uploads one compressed screenshot to MongoDB (see taskStatusRoutes.js)
  // and returns its small, permanent URL -- what actually gets attached
  // to the comment (and stored in DynamoDB) is this URL, never the raw
  // image data, so the comment thread's own size never grows no matter
  // how many screenshots a task accumulates over its lifetime.
  async function uploadCommentImage(taskId, dataUrl) {
    const res = await fetch(apiUrl("/api/tasks/comment-image"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ taskId, dataUrl }),
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || "Couldn't upload that image.");
    return json.url;
  }

  async function addImagesFromFiles(files) {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    const room = MAX_COMMENT_IMAGES - pendingImages.length;
    if (room <= 0) {
      setImageError(`You can attach up to ${MAX_COMMENT_IMAGES} images per comment.`);
      return;
    }
    setImageError(null);
    setProcessingImages(true);
    try {
      const toProcess = list.slice(0, room);
      const results = await Promise.allSettled(
        toProcess.map(async (f) => {
          const compressed = await fileToCompressedDataUrl(f);
          return uploadCommentImage(openCommentTaskId, compressed);
        })
      );
      const added = [];
      const failures = [];
      results.forEach((r) => {
        if (r.status === "fulfilled") added.push({ dataUrl: r.value, id: `${Date.now()}-${Math.random()}` });
        else failures.push(r.reason?.message || "Couldn't process one of the images.");
      });
      if (added.length > 0) setPendingImages((prev) => [...prev, ...added]);
      if (failures.length > 0) setImageError(failures[0]);
      if (list.length > room) {
        setImageError((prev) => prev || `Only added ${room} — you can attach up to ${MAX_COMMENT_IMAGES} images per comment.`);
      }
    } finally {
      setProcessingImages(false);
    }
  }

  function handleCommentPaste(e) {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items.filter((it) => it.type.startsWith("image/")).map((it) => it.getAsFile()).filter(Boolean);
    if (imageFiles.length > 0) {
      e.preventDefault(); // this is a screenshot paste, not text — don't also dump it as text
      addImagesFromFiles(imageFiles);
    }
  }

  function removePendingImage(id) {
    const img = pendingImages.find((x) => x.id === id);
    if (img) discardUploadedImage(img.dataUrl);
    setPendingImages((prev) => prev.filter((x) => x.id !== id));
    setImageError(null);
  }

  function removeAllPendingImages() {
    pendingImages.forEach((img) => discardUploadedImage(img.dataUrl));
    setPendingImages([]);
    setImageError(null);
  }

  // Swap one attached screenshot for another file, keeping its place in the row.
  function startReplacePendingImage(id) {
    replaceTargetIdRef.current = id;
    replaceFileInputRef.current?.click();
  }
  async function replacePendingImage(file) {
    const id = replaceTargetIdRef.current;
    replaceTargetIdRef.current = null;
    if (!id || !file || !file.type.startsWith("image/")) return;
    setImageError(null);
    setProcessingImages(true);
    try {
      const compressed = await fileToCompressedDataUrl(file);
      const url = await uploadCommentImage(openCommentTaskId, compressed);
      setPendingImages((prev) => prev.map((img) => {
        if (img.id !== id) return img;
        discardUploadedImage(img.dataUrl);
        return { dataUrl: url, id: `${Date.now()}-${Math.random()}` };
      }));
    } catch (err) {
      setImageError(err?.message || "Couldn't replace that image.");
    } finally {
      setProcessingImages(false);
    }
  }

  function handleSendComment() {
    const text = composeText.trim();
    if (processingImages) return; // wait for the screenshot upload to finish
    if ((!text && pendingImages.length === 0) || !openCommentTaskId) return;
    addCommentAction(openCommentTaskId, text, pendingImages.map((img) => img.dataUrl));
    setComposeText("");
    setPendingImages([]);
    setImageError(null);
  }

  const openChecklistRow = openChecklistTaskId
    ? allDetailRows.find((r) => r.taskId === openChecklistTaskId)
    : null;
  const openCommentRow = openCommentTaskId
    ? allDetailRows.find((r) => r.taskId === openCommentTaskId)
    : null;
  const openLogsRow = openLogsTaskId
    ? allDetailRows.find((r) => r.taskId === openLogsTaskId)
    : null;
  const openChecklistBookingRatingBucket = openChecklistRow
    ? getRatingBucket("booking", ranklyByMasterPlatform.get(String(openChecklistRow.masterId))?.get("booking")?.rating)
    : "none";

  if (loading) {
    return <div className="rd-blank-tab">Loading properties...</div>;
  }
  if (error) {
    return <div className="rd-blank-tab">{error}</div>;
  }

  return (
    <>
      <div className="rd-filter-bar task-filter-bar">
        <style>{TASK_FILTER_CSS}</style>
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
        <div className="rd-filter-row">
          <div className="rd-filter-group rd-search-group">
            <FilterLabel label="Search" onClear={() => setSearch("")} />
            <div className="rd-search-input-wrap">
              <Search size={14} className="rd-search-icon" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Property, agent, or task..."
                className="rd-search-input"
              />
              {search && (
                <button onClick={() => setSearch("")} className="rd-search-clear-btn" title="Clear search">
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          <MultiSelectFilter label="Agent" options={agents} selected={selectedAgents}
            onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
          <MultiSelectFilter label="Status" options={ALL_STATUSES} selected={selectedStatuses}
            onChange={setSelectedStatuses} onClear={() => setSelectedStatuses([])} />
          <MultiSelectFilter label="Location" options={locations} selected={selectedLocations}
            onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
          <MultiSelectFilter label="Task Type" options={taskTypeOptions} selected={selectedTaskTypes}
            onChange={setSelectedTaskTypes} onClear={() => setSelectedTaskTypes([])} />

          <div className="rd-filter-group rd-pct-filter-group task-pct-filter-group">
            <FilterLabel label="% Rev" onClear={() => setRevPctFilter({ operator: "lte", value: "" })} />
            <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
              {[30, 50, 70, 90].map((threshold) => {
                const isActive = revPctFilter.operator === "lte" && Number(revPctFilter.value) === threshold;
                return (
                  <button
                    key={threshold}
                    onClick={() =>
                      setRevPctFilter((prev) =>
                        prev.operator === "lte" && Number(prev.value) === threshold
                          ? { operator: "lte", value: "" }
                          : { operator: "lte", value: threshold }
                      )
                    }
                    className="rd-dropdown-btn"
                    style={{
                      width: "auto", flex: "0 0 auto",
                      ...(isActive ? { backgroundColor: "#1e293b", color: "#fff", borderColor: "#1e293b" } : {}),
                    }}
                  >
                    ≤{threshold}%
                  </button>
                );
              })}
              <select
                value={revPctFilter.operator}
                onChange={(e) => setRevPctFilter((prev) => ({ ...prev, operator: e.target.value }))}
                className="rd-dropdown-btn"
                title="Comparison"
                style={{ width: "auto", flex: "0 0 auto" }}
              >
                <option value="eq">=</option>
                <option value="gt">&gt;</option>
                <option value="gte">&ge;</option>
                <option value="lt">&lt;</option>
                <option value="lte">&le;</option>
              </select>
              <input
                type="number"
                value={revPctFilter.value}
                onChange={(e) => setRevPctFilter((prev) => ({ ...prev, value: e.target.value }))}
                placeholder="%"
                className="rd-dropdown-btn"
                style={{ width: 58, flex: "0 0 auto" }}
              />
            </div>
          </div>

          <div className="rd-filter-group task-closed-group" style={{ width: "auto" }}>
            <FilterLabel label="Closed" />
            <label
              style={{
                display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155",
                cursor: "pointer", height: 34, whiteSpace: "nowrap",
              }}
            >
              <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              Show closed tasks
            </label>
          </div>

          <div className="rd-actions">
            <button onClick={clearAllFilters} className="rd-btn-clear" disabled={!hasActiveFilters}
              style={hasActiveFilters ? undefined : { opacity: 0.5, cursor: "default" }}>
              <X size={13} />
              Clear all filters
            </button>
          </div>
        </div>
        </div>
      </div>

      <div className="rd-matrix-section">
        <style>{TASK_TABLE_CSS}</style>
        <div className="occ-table-toolbar">
          <button
            type="button"
            onClick={toggleExpandAllProperties}
            className="occ-expand-all-btn"
            title={allPropertiesExpanded ? "Collapse all properties" : "Expand all properties"}
          >
            <ChevronsUpDown size={13} />
            {allPropertiesExpanded ? "Collapse all" : "Expand all"}
          </button>
        </div>
        <div className="rd-matrix-card">
          <div className="rd-matrix-scroll">
            <table className="rd-table">
              <thead>
                <tr>
                  <th
                    className="rd-th-fixed task-th-property"
                    style={{ position: "sticky", left: 0, width: 260, cursor: "pointer" }}
                    onClick={handleRevenueSortClick}
                    title="Click to sort by revenue: ▲ low → high, ▼ high → low, then back to A–Z"
                  >
                    <span className="rd-count-badge" title="Number of different properties with tasks (each property counted once)">{distinctPropertyCount}</span>
                    PROPERTY NAME
                    {sortKey === "revpct" && (
                      <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                  <th className="rd-th-fixed task-th-col" style={{ width: 190 }} title="When the task first appeared, and how many days it has been open (or took, once closed)">CREATED</th>
                  <th className="rd-th-fixed task-th-col" style={{ width: 150 }}>DUE DATE</th>
                  <th className="rd-th-fixed task-th-col" style={{ width: 160 }}>ASSIGNEE</th>
                  <th
                    className="rd-th-fixed task-th-col"
                    style={{ width: 260, cursor: "pointer" }}
                    onClick={() => handleHeaderClick("status")}
                  >
                    STATUS
                    {sortKey === "status" && (
                      <span className="rd-sort-indicator">{sortDirection === "asc" ? "▲" : "▼"}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "today", label: "Today" },
                  { key: "overdue", label: "Overdue" },
                ].map(({ key, label }) => {
                  const sectionGroups = sectionedPropertyGroups[key];
                  if (sectionGroups.length === 0) return null;
                  const isCollapsed = !!collapsedSections[key];
                  return (
                    <React.Fragment key={key}>
                      <tr onClick={() => toggleSection(key)} style={{ cursor: "pointer", background: "#eef2f7" }}>
                        <td colSpan={5} style={{ padding: "8px 16px", fontWeight: 700, color: key === "overdue" ? "#b91c1c" : "#073b4c", fontSize: 13 }}>
                          {/* Sticky label: stays in view when the table is swiped sideways on a phone */}
                          <span className="task-section-label">
                          <span
                            style={{
                              display: "inline-block",
                              transform: isCollapsed ? "rotate(-90deg)" : "none",
                              transition: "transform 0.15s",
                              marginRight: 8,
                            }}
                          >
                            ▾
                          </span>
                          {label}
                          <span
                            style={{
                              marginLeft: 8, padding: "1px 8px", borderRadius: 999,
                              background: "#e2e8f0", color: "#334155", fontSize: 11, fontWeight: 600,
                            }}
                          >
                            {sectionGroups.length}
                          </span>
                          </span>
                        </td>
                      </tr>
                      {!isCollapsed && sectionGroups.map((g) => {
                        const statusSummary = summarizePropertyStatuses(g.tasks);
                        const avatarColor = colorForValue(g.agent) || "#e2e8f0";
                        const rowBg = "#fff"; // property rows are white; the light-blue tint marks an open property's tasks
                        const isExpanded = expandedPropertyKeys.has(propertyKey(key, g.masterId));
                        return (
                          <React.Fragment key={g.masterId}>
                          <tr
                            className={`task-prop-row${isExpanded ? " is-open" : ""}`}
                            onClick={() => togglePropertyExpanded(key, g.masterId)}
                            title={isExpanded ? "Hide this property's tasks" : "Show this property's tasks"}
                          >
                            <td className="rd-td-fixed task-td-property" style={{ position: "sticky", left: 0, padding: "10px 16px", background: rowBg }}>
                              <span className="rev-legend-swatch" style={{ backgroundColor: colorForValue(g.property) }} />
                              {g.property}
                              <ChevronDown size={12} className={`occ-expand-icon ${isExpanded ? "occ-expanded" : ""}`} />
                              <span
                                style={{
                                  marginLeft: 8, padding: "1px 7px", borderRadius: 999,
                                  background: "#e0e7ff", color: "#3730a3", fontSize: 11, fontWeight: 600,
                                }}
                              >
                                {g.tasks.length} task{g.tasks.length === 1 ? "" : "s"}
                              </span>
                              {/* MTD badge only next to the task types that use MTD. */}
                              {g.tasks.some((t) => MTD_GATED_TASK_KINDS.has(t.kind)) && (
                              <span
                                title={g.mtdPct === null
                                  ? "No MTD revenue target for this property"
                                  : `MTD revenue: ${g.mtdPct.toFixed(1)}% of target (1st of this month to today)`}
                                style={{
                                  marginLeft: 6, padding: "1px 7px", borderRadius: 999,
                                  background: getMtdRevColor(g.mtdPct), color: "#1e293b",
                                  fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                                }}
                              >
                                MTD {g.mtdPct === null ? "—" : `${Math.round(g.mtdPct)}%`}
                              </span>
                              )}
                            </td>
                            <td style={{ padding: "10px 16px", background: rowBg, color: "#475569", fontSize: 13 }}>
                              {formatDueDateRange(g.minCreatedDate, g.maxCreatedDate)}
                            </td>
                            <td style={{ padding: "10px 16px", background: rowBg, color: key === "overdue" ? "#dc2626" : "#334155", fontSize: 13 }}>
                              {formatDueDateRange(g.minDueDate, g.maxDueDate)}
                            </td>
                            <td style={{ padding: "10px 16px", background: rowBg }}>
                              <span
                                title={g.agent}
                                style={{
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  width: 28, height: 28, borderRadius: "50%", background: avatarColor,
                                  fontSize: 11, fontWeight: 700, color: "#1e293b",
                                }}
                              >
                                {getAgentInitials(g.agent)}
                              </span>
                            </td>
                            <td style={{ padding: "8px 16px", background: rowBg }}>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {statusSummary.map(({ status, count }) => {
                                  const badge = statusBadgeStyle(status);
                                  return (
                                    <span
                                      key={status}
                                      style={{
                                        padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                                        background: badge.bg, color: badge.color, whiteSpace: "nowrap",
                                      }}
                                    >
                                      {count} {status}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && groupTasksByKind(g.tasks).map((group) => (
                            <React.Fragment key={group.kind}>
                              <tr className="task-kind-row task-open-block">
                                <td colSpan={5}>
                                  <span className="task-section-label" style={{ gap: 8, flexWrap: "wrap" }}>
                                    <span
                                      className="task-kind-label"
                                      style={{ color: TASK_KIND_COLORS[group.kind] || "#334155", borderColor: `${TASK_KIND_COLORS[group.kind] || "#94a3b8"}55` }}
                                    >
                                      <span className="task-kind-dot" style={{ background: TASK_KIND_COLORS[group.kind] || "#94a3b8" }} />
                                      {group.title}
                                    </span>
                                    {group.source && (
                                      <span
                                        title={`Generated from the ${group.source} data`}
                                        style={{ fontSize: 10.5, fontWeight: 600, color: "#5b21b6", background: "#ede9fe", borderRadius: 999, padding: "1px 8px" }}
                                      >
                                        from {group.source}
                                      </span>
                                    )}
                                    <span style={{ fontSize: 11, fontWeight: 700, color: "#334155", background: "#e2e8f0", borderRadius: 999, padding: "1px 8px" }}>
                                      {group.tasks.length}
                                    </span>
                                  </span>
                                </td>
                              </tr>
                              {group.tasks.map((r) => renderTaskRow(r))}
                            </React.Fragment>
                          ))}
                          {isExpanded && (
                            <tr className="task-block-end" aria-hidden="true">
                              <td colSpan={5} />
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {totalPropertyRows === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: "24px 16px", textAlign: "center", color: "#94a3b8" }}>
                      No tasks match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {rejectingTaskId && (
        <div
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100,
          }}
          onClick={() => !rejectSaving && setRejectingTaskId(null)}
        >
          <div
            style={{
              backgroundColor: "#fff", borderRadius: 8, padding: 20, width: "90%", maxWidth: 440,
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, color: "#b91c1c", fontSize: 15 }}>Reject task</h3>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4, marginBottom: 12 }}>
              {rows.find((x) => x.id === rejectingTaskId)?.property || ""}
              {" — the task is marked Rejected and the assignee sees this reason."}
            </div>
            <textarea
              autoFocus
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejecting (required)"
              rows={4}
              style={{
                width: "100%", boxSizing: "border-box", padding: 8, fontSize: 13,
                border: "1px solid #cbd5e1", borderRadius: 6, resize: "vertical", fontFamily: "inherit",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setRejectingTaskId(null)}
                disabled={rejectSaving}
                style={{
                  padding: "6px 12px", fontSize: 13, borderRadius: 6, border: "1px solid #cbd5e1",
                  background: "#fff", color: "#334155", cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReject}
                disabled={!rejectReason.trim() || rejectSaving}
                style={{
                  padding: "6px 12px", fontSize: 13, fontWeight: 700, borderRadius: 6, border: "none",
                  background: !rejectReason.trim() || rejectSaving ? "#fca5a5" : "#dc2626", color: "#fff",
                  cursor: !rejectReason.trim() || rejectSaving ? "default" : "pointer",
                }}
              >
                {rejectSaving ? "Rejecting…" : "Reject task"}
              </button>
            </div>
          </div>
        </div>
      )}

      {openChecklistRow && (
        <div
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100,
          }}
          onClick={() => setOpenChecklistTaskId(null)}
        >
          <div
            style={{
              backgroundColor: "#fff", borderRadius: 8, padding: 20, width: "90%",
              maxWidth: 420, maxHeight: "80vh", overflow: "auto",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, color: "#073b4c", fontSize: 15 }}>{openChecklistRow.label} Checklist</h3>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{openChecklistRow.property}</div>
              </div>
              <button
                type="button"
                onClick={() => setOpenChecklistTaskId(null)}
                aria-label="Close"
                style={{ background: "none", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer", color: "#333" }}
              >
                ×
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(getChecklistItemsForMetric(openChecklistRow.kind, openChecklistRow.label, openChecklistBookingRatingBucket) || []).map((item) => {
                const checked = !!checklistProgress[openChecklistRow.taskId]?.[item];
                return (
                  <label
                    key={item}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, fontSize: 13,
                      color: checked ? "#166534" : "#334155", cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleChecklistItem(openChecklistRow.taskId, item)}
                    />
                    <span style={{ textDecoration: checked ? "line-through" : "none" }}>{item}</span>
                  </label>
                );
              })}
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: "#64748b" }}>
              {isChecklistComplete(openChecklistRow)
                ? "✓ All items complete — this task can now be marked Under Review."
                : "Complete every item to unlock marking this task Under Review."}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setOpenChecklistTaskId(null)}
                style={{
                  padding: "8px 14px", borderRadius: 6, border: "none",
                  background: "#073b4c", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {openLogsRow && (
        <div
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100,
          }}
          onClick={() => setOpenLogsTaskId(null)}
        >
          <div
            style={{
              backgroundColor: "#fff", borderRadius: 10, padding: 20, width: "94%", maxWidth: 460,
              maxHeight: "80vh", display: "flex", flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  Activity log
                </div>
                <h3 style={{ margin: 0, fontSize: 15, color: "#073b4c" }}>{openLogsRow.taskText}</h3>
                <div style={{ fontSize: 12, color: "#64748b" }}>{openLogsRow.property}</div>
              </div>
              <button
                type="button"
                onClick={() => setOpenLogsTaskId(null)}
                aria-label="Close"
                style={{ background: "none", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer", color: "#333" }}
              >
                ×
              </button>
            </div>

            <div style={{ overflowY: "auto", paddingRight: 4 }}>
              {(openLogsRow.logs || []).length === 0 && (
                <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "20px 0" }}>
                  No activity recorded yet.
                </div>
              )}
              {[...(openLogsRow.logs || [])].reverse().map((l, i, arr) => (
                <div key={i} style={{ display: "flex", gap: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", marginTop: 4, background: LOG_COLORS[l.to] || "#94a3b8" }} />
                    {i < arr.length - 1 && <div style={{ flex: 1, width: 1, background: "#cbd5e1" }} />}
                  </div>
                  <div style={{ paddingBottom: 14, fontSize: 12 }}>
                    <div><b>{l.by}</b> {LOG_LABELS[l.type] || "updated the task"}</div>
                    {(l.from || l.to) && (
                      <div style={{ color: "#475569" }}>{l.from || "—"} → {l.to || "—"}</div>
                    )}
                    {l.reason && <div style={{ color: "#b91c1c" }}>Reason: {l.reason}</div>}
                    <div style={{ color: "#94a3b8" }}>{formatCommentTimestamp(l.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {openCommentRow && (
        <div
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100,
          }}
          onClick={closeCommentThread}
        >
          <div
            style={{
              backgroundColor: "#fff", borderRadius: 10, padding: 22, width: "94%",
              maxWidth: 760, maxHeight: "88vh", display: "flex", flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div
                  style={{
                    fontSize: 10, fontWeight: 700, color: "#3730a3", textTransform: "uppercase",
                    letterSpacing: "0.03em", marginBottom: 3,
                  }}
                >
                  {TASK_KIND_MODAL_CONFIG[openCommentRow.kind]?.title || openCommentRow.kind}
                  {TASK_KIND_MODAL_CONFIG[openCommentRow.kind]?.source && (
                    <span style={{ color: "#6d28d9", textTransform: "none", fontWeight: 600 }}>
                      {" · from "}{TASK_KIND_MODAL_CONFIG[openCommentRow.kind].source}
                    </span>
                  )}
                </div>
                <h3 style={{ margin: 0, color: "#073b4c", fontSize: 15 }}>{openCommentRow.taskText}</h3>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{openCommentRow.property}</div>
              </div>
              <button
                type="button"
                onClick={closeCommentThread}
                aria-label="Close"
                style={{ background: "none", border: "none", fontSize: 20, lineHeight: 1, cursor: "pointer", color: "#333" }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                flex: "1 1 auto", overflowY: "auto", minHeight: 260, maxHeight: "58vh",
                border: "1px solid #e2e8f0", borderRadius: 8, padding: 10,
                display: "flex", flexDirection: "column", gap: 10, background: "#f8fafc",
              }}
            >
              {(openCommentRow.comments || []).length === 0 && (
                <div style={{ fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "16px 0" }}>
                  No comments yet — add the first one below.
                </div>
              )}
              {(openCommentRow.comments || []).map((c, i) => (
                <div key={i} style={{ background: c.system ? "#fffbeb" : "#fff", borderRadius: 8, padding: "8px 10px", border: `1px solid ${c.system ? "#fde68a" : "#e2e8f0"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#073b4c" }}>{c.username}</span>
                    <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
                      {formatCommentTimestamp(c.timestamp)}
                    </span>
                  </div>
                  {c.text && <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap" }}>{c.text}</div>}
                  {(c.images || []).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: c.text ? 6 : 0 }}>
                      {c.images.map((src, imgIdx) => (
                        <button
                          key={imgIdx}
                          type="button"
                          onClick={() => setPreviewImageSrc(src)}
                          title="View full size"
                          style={{ padding: 0, border: "none", background: "none", cursor: "pointer" }}
                        >
                          <img
                            src={src}
                            alt="Attached screenshot"
                            style={{ maxWidth: 140, maxHeight: 140, borderRadius: 6, border: "1px solid #e2e8f0", display: "block" }}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {(pendingImages.length > 0 || processingImages) && (
              <div style={{ marginTop: 10, padding: "8px 10px", border: "1px dashed #cbd5e1", borderRadius: 8, background: "#f8fafc" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Attached ({pendingImages.length}/{MAX_COMMENT_IMAGES}) — not sent yet
                  </span>
                  {pendingImages.length > 1 && (
                    <button
                      type="button"
                      onClick={removeAllPendingImages}
                      style={{ border: "none", background: "none", color: "#b91c1c", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
                    >
                      Remove all
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {pendingImages.map((img, i) => (
                    <div key={img.id} style={{ width: 76 }}>
                      <div style={{ position: "relative", width: 76, height: 76 }}>
                        <img
                          src={img.dataUrl}
                          alt={`Attached screenshot ${i + 1}`}
                          title="Click to view full size"
                          onClick={() => setPreviewImageSrc(img.dataUrl)}
                          style={{
                            width: 76, height: 76, objectFit: "cover", borderRadius: 6,
                            border: "1px solid #cbd5e1", display: "block", cursor: "zoom-in", background: "#fff",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removePendingImage(img.id)}
                          aria-label={`Remove screenshot ${i + 1}`}
                          title="Remove this screenshot"
                          style={{
                            position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%",
                            background: "#dc2626", color: "#fff", border: "2px solid #fff", fontSize: 12, fontWeight: 700,
                            lineHeight: "16px", cursor: "pointer", padding: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => startReplacePendingImage(img.id)}
                        disabled={processingImages}
                        title="Choose a different image instead of this one"
                        style={{
                          width: "100%", marginTop: 4, padding: "2px 0", border: "1px solid #e2e8f0", borderRadius: 4,
                          background: "#fff", color: "#334155", fontSize: 11, fontWeight: 600,
                          cursor: processingImages ? "not-allowed" : "pointer",
                        }}
                      >
                        Replace
                      </button>
                    </div>
                  ))}
                  {processingImages && (
                    <div
                      style={{
                        width: 76, height: 76, borderRadius: 6, border: "1px dashed #94a3b8", background: "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, color: "#64748b", textAlign: "center",
                      }}
                    >
                      Uploading…
                    </div>
                  )}
                </div>
              </div>
            )}
            {imageError && (
              <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 6 }}>{imageError}</div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <input
                ref={replaceFileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  replacePendingImage(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <input
                ref={commentFileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  addImagesFromFiles(e.target.files);
                  e.target.value = ""; // lets picking the same file again re-trigger onChange
                }}
              />
              <button
                type="button"
                onClick={() => commentFileInputRef.current?.click()}
                title="Attach an image"
                aria-label="Attach an image"
                style={{
                  padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0",
                  background: "#f8fafc", color: "#334155", fontSize: 16, cursor: "pointer", alignSelf: "flex-end",
                }}
              >
                📷
              </button>
              <textarea
                value={composeText}
                onChange={(e) => setComposeText(e.target.value)}
                onPaste={handleCommentPaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendComment();
                  }
                }}
                placeholder="Write a comment... (paste a screenshot with Ctrl+V)"
                rows={4}
                style={{
                  flex: "1 1 auto", padding: "10px 12px", borderRadius: 8, minHeight: 84,
                  border: "1px solid #cbd5e1", fontSize: 14, fontFamily: "inherit", resize: "vertical",
                }}
              />
              <button
                type="button"
                onClick={handleSendComment}
                disabled={processingImages || (!composeText.trim() && pendingImages.length === 0)}
                title={processingImages ? "Wait for the screenshot to finish uploading" : undefined}
                style={{
                  padding: "8px 14px", borderRadius: 6, border: "none",
                  background: !processingImages && (composeText.trim() || pendingImages.length > 0) ? "#073b4c" : "#cbd5e1",
                  color: "#fff", fontSize: 13, fontWeight: 600,
                  cursor: !processingImages && (composeText.trim() || pendingImages.length > 0) ? "pointer" : "not-allowed",
                  alignSelf: "flex-end",
                }}
              >
                {processingImages ? "Uploading…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewImageSrc && (
        <div
          style={{
            position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200,
            padding: 24,
          }}
          onClick={() => setPreviewImageSrc(null)}
        >
          <button
            type="button"
            onClick={() => setPreviewImageSrc(null)}
            aria-label="Close"
            style={{
              position: "absolute", top: 16, right: 20, background: "none", border: "none",
              fontSize: 28, lineHeight: 1, cursor: "pointer", color: "#fff",
            }}
          >
            ×
          </button>
          <img
            src={previewImageSrc}
            alt="Full size attachment"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 8, boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}
          />
        </div>
      )}
    </>
  );
}
